import CourseReviewForm from "./CourseReviewForm";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Alert, Badge, Button, Card, Col, Modal, Row, Spinner } from "react-bootstrap";
import { BsCheckLg, BsPlayCircle, BsCalendar2Heart, BsCalendar2Check, BsChevronRight, BsChevronDown, BsCalendar2Event, BsPatchCheck } from "react-icons/bs";

import { app, db } from "../../shared/libs/firebaseConfig";
import { useAuth } from "../../shared/auth/AuthProvider";
import {
  confirmReplaceAccessDevice,
  runCourseAccessCheck,
} from "../../shared/utils/accessControl";

import {
  claimCourseActiveDevice,
  heartbeatCourseActiveDevice,
  subscribeCourseActiveDevice,
} from "../../shared/utils/courseActiveDevice";

import "./CourseView.css";
import {
  PLAYBACK_PROVIDER,
  canOfferMuxPreview,
  canRequestMuxPlaybackCredential,
  clearLearnerProviderPreference,
  credentialIsUsable,
  initialPlaybackProvider,
  learnerStandbyUiVisible,
  readLearnerProviderPreference,
  resolveActivePlaybackProvider,
  switchPreviewProvider,
  writeLearnerProviderPreference,
} from "./playbackProviderState";
import {
  isMuxPlaybackStartupSuccessEvent,
  shouldShowHlsJsPlaybackFailure,
} from "./muxPlaybackErrorState";
import {
  codecDiagnostics,
  createMuxDiagnosticAttempt,
  safeFlushMuxDiagnostic,
  safeMuxDiagnostic,
  sanitizeHlsFatalError,
} from "./muxPlaybackDiagnostics";

import { attachPlaybackRuntimeDiagnostics, runtimeEndReason } from "./playbackRuntimeDiagnostics";
import { isTransientCourseRefreshError } from "./courseViewRefreshError";
import {
  MUX_PLAYBACK_ENGINE,
  detectMuxPlaybackEnvironment,
  selectMuxPlaybackEngine,
  shouldProbeHlsJsFirst,
} from "./muxPlaybackEngine";

import { loadCurriculumChildren } from "./courseViewCurriculumScheduler";
import { createLessonLoadDiagnostic, observeLessonLoad } from "./courseViewLessonLoadDiagnostics";

const STATIC_QR_IMAGE_URL = "/images/qr-30-4-69.jpg";

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function canShowLesson(item, isPreviewMode) {
  if (!item) return false;
  if (item.isActive === false) return false;
  if (isPreviewMode) return true;
  if (item.isPreview) return true;
  return item.contentStatus === "published";
}

function getAllowedDownloadScopesFromPackage(pkg) {
  switch (pkg) {
    case "F_2":
    case "P_2":
      return ["general", "long_civil", "long_criminal", "summary_civil", "summary_criminal"];

    case "F_1_civil":
    case "P_1_civil":
      return ["general", "long_civil", "summary_civil"];

    case "F_1_criminal":
    case "P_1_criminal":
      return ["general", "long_criminal", "summary_criminal"];

    case "L_2":
      return ["general", "long_civil", "long_criminal"];
    case "L_1_civil":
      return ["general", "long_civil"];
    case "L_1_criminal":
      return ["general", "long_criminal"];

    case "S_2":
      return ["general", "summary_civil", "summary_criminal"];
    case "S_1_civil":
      return ["general", "summary_civil"];
    case "S_1_criminal":
      return ["general", "summary_criminal"];

    default:
      return ["general"];
  }
}

const DOWNLOAD_SCOPE_LABELS = {
  general: "เอกสารทั่วไป",
  long_civil: "เอกสารเนติบัณฑิต 1/79 ขาแพ่ง",
  long_criminal: "เอกสารเนติบัณฑิต 1/79 ขาอาญา",
  summary_civil: "เอกสารสรุป 1/79 แพ่ง",
  summary_criminal: "เอกสารสรุป 1/79 อาญา",
};

function getDownloadScopeLabel(scope) {
  return DOWNLOAD_SCOPE_LABELS[String(scope || "general")] || String(scope || "general");
}

const SPECIAL_DOWNLOAD_SCOPES = new Set(["statute_civil", "statute_criminal"]);

function isRealPdfFile(item) {
  const fileHref = String(item?.url || item?.fileUrl || "").trim();
  const ext = String(item?.ext || "").toLowerCase().trim();
  const fileName = String(item?.fileName || item?.name || "").toLowerCase().trim();

  return (
    item?.isActive !== false &&
    !!fileHref &&
    (ext === "pdf" || fileName.endsWith(".pdf") || fileHref.toLowerCase().includes(".pdf"))
  );
}

function getUploadedAtMillis(value) {
  try {
    const date = value?.toDate ? value.toDate() : new Date(value || "");
    const millis = date.getTime();
    return Number.isFinite(millis) ? millis : -1;
  } catch {
    return -1;
  }
}

function resolveSpecialPdf(items, scope) {
  return safeArray(items)
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item?.scope === scope)
    .sort((a, b) => {
      const validDifference = Number(isRealPdfFile(b.item)) - Number(isRealPdfFile(a.item));
      if (validDifference) return validDifference;
      return (
        getUploadedAtMillis(b.item?.uploadedAt) -
          getUploadedAtMillis(a.item?.uploadedAt) ||
        a.index - b.index
      );
    })[0]?.item || null;
}

function isVideoReady(video = {}) {
  return video?.isReady === true || video?.readyToStream === true || video?.status === "ready";
}

function formatThaiFullDate(value) {
  try {
    if (!value) return "";
    let d = null;

    if (typeof value === "string") {
      d = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
    } else if (value?.toDate) {
      d = value.toDate();
    } else if (value instanceof Date) {
      d = value;
    }

    if (!d || Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("th-TH", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function getLessonBulletStatus(item) {
  const video = item?.video || {};
  const ready = isVideoReady(video);
  const published = item?.contentStatus === "published";

  if (ready && published) return "published";
  if (ready && !published) return "scheduled";
  return "empty";
}

function getLessonBulletStyle(status) {
  switch (status) {
    case "published":
      return {
        background: "#198754",
        boxShadow: "0 0 0 4px rgba(25,135,84,.12)",
      };
    case "scheduled":
      return {
        background: "#fd7e14",
        boxShadow: "0 0 0 4px rgba(253,126,20,.12)",
      };
    default:
      return {
        background: "#adb5bd",
        boxShadow: "0 0 0 4px rgba(173,181,189,.12)",
      };
  }
}

function formatVideoDurationHours(durationSec) {
  const totalSec = Number(durationSec || 0);
  if (!Number.isFinite(totalSec) || totalSec <= 0) return "";
  const totalMin = Math.round(totalSec / 60);
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  return `${hours}:${String(mins).padStart(2, "0")}`;
}

function getLessonMeta(item) {
  const bulletStatus = getLessonBulletStatus(item);
  const published = bulletStatus === "published";
  const durationText = formatVideoDurationHours(item?.video?.durationSec);

  if (published && durationText) {
    return { text: durationText, kind: "duration" };
  }

  return {
    text: formatThaiFullDate(item?.releaseDateText || item?.releaseAt || "") || "—",
    kind: "date",
  };
}

function getLessonProgressState(progressDoc, lessonId) {
  const lessonStates = progressDoc?.lessonStates || {};
  return lessonStates?.[lessonId] || null;
}

function isLessonCompleted(item, progressDoc) {
  const st = getLessonProgressState(progressDoc, item?.id);
  return !!st?.completed;
}

function isLessonStarted(item, progressDoc) {
  const st = getLessonProgressState(progressDoc, item?.id);
  return Number(st?.lastVideoTimeSec || 0) > 0 && !st?.completed;
}

function getLessonIndicator(item, progressDoc) {
  const bulletStatus = getLessonBulletStatus(item);

  if (bulletStatus !== "published") {
    return { kind: "bullet", status: bulletStatus };
  }
  if (isLessonCompleted(item, progressDoc)) {
    return { kind: "completed" };
  }
  if (isLessonStarted(item, progressDoc)) {
    return { kind: "in_progress" };
  }
  return { kind: "bullet", status: "published" };
}

function flattenVisibleLessons(visibleHeaders) {
  const rows = [];

  safeArray(visibleHeaders).forEach((h, hi) => {
    safeArray(h.sections).forEach((sec, si) => {
      safeArray(sec.items).forEach((it, li) => {
        rows.push({
          ...it,
          __headerIndex: hi,
          __sectionIndex: si,
          __lessonIndex: li,
          __headerTitle: h?.title || "",
          __sectionTitle: sec?.title || "",
        });
      });
    });
  });

  return rows;
}

function getNextLessonFromVisibleHeaders(visibleHeaders, currentLessonId) {
  const flat = flattenVisibleLessons(visibleHeaders);
  const idx = flat.findIndex((x) => x.id === currentLessonId);
  if (idx < 0) return null;
  return flat[idx + 1] || null;
}

function getCurrentUnpaidInstallmentFromEnrolls(enrolls = []) {
  for (const enroll of safeArray(enrolls)) {
    if (enroll?.paymentType !== "installment") continue;

    const installments = Array.isArray(enroll?.installments) ? enroll.installments : [];
    const unpaid = installments.find((item) => item?.status !== "paid");

    if (unpaid) {
      return {
        ...unpaid,
        enrollId: enroll?.id || "",
        courseId: enroll?.courseId || "",
      };
    }
  }

  return null;
}

function isPastDueInstallment(dueAt) {
  try {
    if (!dueAt) return false;
    const d = dueAt?.toDate ? dueAt.toDate() : new Date(dueAt);
    if (Number.isNaN(d.getTime())) return false;
    return Date.now() > d.getTime();
  } catch {
    return false;
  }
}

function getCurrentUnpaidInstallment(enroll) {
  const installments = Array.isArray(enroll?.installments) ? enroll.installments : [];
  return installments.find((item) => item?.status !== "paid") || null;
}

function isInstallmentEnrollOverdue(enroll) {
  if (enroll?.paymentType !== "installment") return false;

  const unpaid = getCurrentUnpaidInstallment(enroll);
  if (!unpaid?.dueAt) return false;

  const d = unpaid.dueAt?.toDate ? unpaid.dueAt.toDate() : new Date(unpaid.dueAt);
  if (Number.isNaN(d.getTime())) return false;

  return Date.now() > d.getTime();
}

function stripLessonPrefix(title = "") {
  return String(title || "").replace(/^\s*\d+\s*[.)]\s*/, "");
}

function stripPdfExt(name = "") {
  return String(name || "").replace(/\.pdf$/i, "");
}

function buildCourseProgressSummary(flatLessons = [], lessonStates = {}) {
  const publishedVideoLessons = safeArray(flatLessons).filter((item) => {
    const durationSec = Number(item?.video?.durationSec || 0);
    return (
      item?.contentStatus === "published" &&
      item?.isActive !== false &&
      isVideoReady(item?.video || {}) &&
      durationSec > 0
    );
  });

  const totalDurationSec = publishedVideoLessons.reduce(
    (sum, item) => sum + Number(item?.video?.durationSec || 0),
    0
  );

  let completedDurationSec = 0;
  let completedLessons = 0;

  for (const item of publishedVideoLessons) {
    const st = lessonStates?.[item.id];
    if (st?.completed === true) {
      completedLessons += 1;
      completedDurationSec += Number(item?.video?.durationSec || 0);
    }
  }

  const percent =
    totalDurationSec > 0
      ? Math.min(100, Math.max(0, Math.round((completedDurationSec / totalDurationSec) * 100)))
      : 0;

  return {
    percent,
    completedDurationSec,
    totalDurationSec,
    completedLessons,
    totalLessons: publishedVideoLessons.length,
    progressSummaryVersion: 1,
  };
}

function MuxFailureHelp({ correlationId }) {
  const [copyStatus, setCopyStatus] = useState("");
  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(correlationId);
      setCopyStatus("คัดลอกแล้ว");
    } catch {
      setCopyStatus("คัดลอกไม่สำเร็จ กรุณาเลือกรหัสตรวจสอบเพื่อคัดลอกด้วยตนเอง");
    }
  };
  return (
    <div className="courseview-mux-failure-help">
      <section aria-label="ลองเปิดด้วย Firefox">
        <strong>ลองเปิดด้วย Firefox</strong>
        <p>หากไม่สามารถรับชมได้ ลองเปิดบทเรียนด้วย Firefox ซึ่งอาจช่วยแก้ปัญหาการเล่นวิดีโอบนอุปกรณ์หรือเบราว์เซอร์บางรุ่นได้</p>
        <a className="btn btn-sm btn-outline-secondary" href="https://www.mozilla.org/firefox/new/" target="_blank" rel="noopener noreferrer">ดาวน์โหลด Firefox</a>
      </section>
      <section aria-label="แจ้งปัญหาให้ทีมงาน">
        <strong>แจ้งปัญหาให้ทีมงาน</strong>
        <p>{correlationId
          ? "เพื่อช่วยทีมงานตรวจสอบสาเหตุ กรุณาคัดลอก “รหัสตรวจสอบ” ด้านล่าง และส่งให้ทีมงานทางเพจ ChicChic Law Tutor"
          : "กรุณาแจ้งอาการที่พบให้ทีมงานทางเพจ ChicChic Law Tutor"}</p>
        {correlationId ? <div className="courseview-mux-diagnostic-id">รหัสตรวจสอบ: <span>{correlationId}</span></div> : null}
        <div className="courseview-mux-help-actions">
          {correlationId ? <button type="button" className="btn btn-sm btn-outline-secondary" onClick={copyId}>คัดลอกรหัสตรวจสอบ</button> : null}
          <a className="btn btn-sm btn-outline-secondary" href="https://web.facebook.com/chicchiclawtutor" target="_blank" rel="noopener noreferrer">แจ้งปัญหาให้ทีมงาน</a>
        </div>
        <div role="status" aria-live="polite">{copyStatus}</div>
      </section>
    </div>
  );
}

export default function CourseView() {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const previewMode = searchParams.get("preview") === "1";

  const { user, userDoc } = useAuth();
  const functions = useMemo(() => getFunctions(app, "asia-southeast1"), []);

  const curriculumAttemptRef = useRef(null);
  const curriculumScope = [courseId, user?.uid || "", previewMode].join("|");
  const curriculumScopeRef = useRef(curriculumScope);
  curriculumScopeRef.current = curriculumScope;
  useEffect(() => () => {
    curriculumAttemptRef.current?.controller.abort();
    curriculumAttemptRef.current = null;
  }, [curriculumScope]);

  const lessonLoadDiagnosticRef = useRef(null);
  useEffect(() => {
    const diagnostic = createLessonLoadDiagnostic();
    lessonLoadDiagnosticRef.current = diagnostic;
    return () => {
      diagnostic.abandon();
      if (lessonLoadDiagnosticRef.current === diagnostic) lessonLoadDiagnosticRef.current = null;
    };
  }, [courseId, previewMode, user?.uid]);

  const [loadingPage, setLoadingPage] = useState(true);
  const [course, setCourse] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [activeLesson, setActiveLesson] = useState(null);
  const [grantedEnrolls, setGrantedEnrolls] = useState([]);

  // const [playerLoading, setPlayerLoading] = useState(false);
  const [playerLoading, setPlayerLoading] = useState(true);
  const [iframeUrl, setIframeUrl] = useState("");
  const [playerErr, setPlayerErr] = useState("");
  const [preferredPlaybackProvider, setPreferredPlaybackProvider] = useState(initialPlaybackProvider);
  const [activePlaybackProvider, setActivePlaybackProvider] = useState(initialPlaybackProvider);
  const [muxAvailability, setMuxAvailability] = useState("idle");
  const [muxCredential, setMuxCredential] = useState(null);
  const [muxPlayerLoading, setMuxPlayerLoading] = useState(false);
  const [muxPlaybackError, setMuxPlaybackError] = useState("");
  const [muxPlaybackDiagnosticId, setMuxPlaybackDiagnosticId] = useState("");
  const [err, setErr] = useState("");
  const [accessReady, setAccessReady] = useState(previewMode);
  const [accessResult, setAccessResult] = useState({
    status: previewMode ? "allowed" : "idle",
    ok: !!previewMode,
    message: "",
    watermarkText: "",
    settings: null,
  });

  const [playerKey, setPlayerKey] = useState(0);
  const [openSidebarHeaderKey, setOpenSidebarHeaderKey] = useState("0");
  const [openSidebarSectionKey, setOpenSidebarSectionKey] = useState("0-0");

  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const [cloudflareIframe, setCloudflareIframe] = useState(null);
  const bindCloudflareIframe = useCallback((node) => {
    iframeRef.current = node;
    setCloudflareIframe(node);
  }, []);
  const cloudflareRestoreRef = useRef(null);
  const cloudflareSourceLessonRef = useRef("");
  const cloudflareCredentialExpRef = useRef();
  const cloudflareLessonRef = useRef(activeLesson);
  useEffect(() => {
    cloudflareLessonRef.current = activeLesson;
  }, [activeLesson]);
  const muxVideoRef = useRef(null);
  // Page-local preference; deliberately absent from streaming effect dependencies.
  const muxPlaybackRateRef = useRef(1);
  const [muxPlaybackRate, setMuxPlaybackRate] = useState(1);
  const [muxPlaybackRateNotice, setMuxPlaybackRateNotice] = useState("");
  const applyMuxPlaybackRate = useCallback((video, rate = muxPlaybackRateRef.current) => {
    if (!video) return;
    try {
      video.playbackRate = rate;
      const actualRate = video.playbackRate;
      muxPlaybackRateRef.current = actualRate;
      setMuxPlaybackRate(actualRate);
      setMuxPlaybackRateNotice(actualRate === rate ? "" : "เบราว์เซอร์นี้ปรับความเร็วได้จำกัด");
    } catch {
      muxPlaybackRateRef.current = video.playbackRate;
      setMuxPlaybackRate(video.playbackRate);
      setMuxPlaybackRateNotice("เบราว์เซอร์นี้ไม่รองรับความเร็วที่เลือก");
    }
  }, []);
  const muxRuntimeRef = useRef(null);
  const bindMuxVideo = useCallback((node) => {
    if (!node) {
      try {
        const current = muxRuntimeRef.current;
        current?.observer.close(runtimeEndReason(current.identity, runtimeIdentityRef.current));
      } catch {}
    }
    muxVideoRef.current = node;
    if (node) applyMuxPlaybackRate(node);
  }, [applyMuxPlaybackRate]);
  const muxDiagnosticAttemptRef = useRef(null);
  const pendingProviderPositionRef = useRef({ time: 0, shouldPlay: false });
  const lastSavedTimeRef = useRef(0);

  const [lastProgress, setLastProgress] = useState(null);

  const lastProgressRef = useRef(null);
  useEffect(() => {
    lastProgressRef.current = lastProgress;
  }, [lastProgress]);

  const [progressLoaded, setProgressLoaded] = useState(false);

  const [curriculumExpandedAll, setCurriculumExpandedAll] = useState(false);

  const [showQrModal, setShowQrModal] = useState(false);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [showChecklistModal, setShowChecklistModal] = useState(false);

  const [schedulePreviewIndex, setSchedulePreviewIndex] = useState(0);
  const [calendarPreviewIndex, setCalendarPreviewIndex] = useState(0);
  const [checklistPreviewIndex, setChecklistPreviewIndex] = useState(0);

  const playerTopRef = useRef(null);

  const [showVideoHelp, setShowVideoHelp] = useState(false);
  const videoHelpTimerRef = useRef(null);

  const [playerFrameLoaded, setPlayerFrameLoaded] = useState(false);

  const currentVideoRequestKeyRef = useRef("");

  const activeLessonIdRef = useRef("");

  useEffect(() => {
    activeLessonIdRef.current = activeLesson?.id || "";
  }, [activeLesson?.id]);

  useEffect(() => {
    const preferred = previewMode
      ? PLAYBACK_PROVIDER.CLOUDFLARE
      : readLearnerProviderPreference({
        storage: window.sessionStorage, courseId, uid: user?.uid,
      });
    setPreferredPlaybackProvider(preferred);
    setActivePlaybackProvider(PLAYBACK_PROVIDER.CLOUDFLARE);
  }, [courseId, previewMode, user?.uid]);

  useEffect(() => {
    setActivePlaybackProvider(PLAYBACK_PROVIDER.CLOUDFLARE);
    setMuxAvailability("idle");
    setMuxCredential(null);
    muxDiagnosticAttemptRef.current = null;
    setMuxPlayerLoading(false);
    setMuxPlaybackError("");
    setMuxPlaybackDiagnosticId("");
    pendingProviderPositionRef.current = { time: 0, shouldPlay: false };
  }, [activeLesson?.id, activeLesson?.headerId, activeLesson?.sectionId]);

  const standbyAvailability = String(
    course?.videoStandbyPlayback?.availability || "disabled"
  );
  const learnerStandbyEnabled = learnerStandbyUiVisible({ previewMode, standbyAvailability });

  useEffect(() => {
    if (!courseId) return undefined;
    return onSnapshot(doc(db, "courses", courseId), (snapshot) => {
      if (!snapshot.exists()) return;
      setCourse((current) => ({ ...(current || {}), id: snapshot.id, ...snapshot.data() }));
    }, (error) => {
      console.error("course policy subscription failed", { code: error?.code || "unknown" });
    });
  }, [courseId]);

  useEffect(() => {
    if (previewMode || !course || !user?.uid || standbyAvailability === "learner_enabled") return;
    setPreferredPlaybackProvider(clearLearnerProviderPreference({
      storage: window.sessionStorage, courseId, uid: user.uid,
    }));
    setActivePlaybackProvider(PLAYBACK_PROVIDER.CLOUDFLARE);
    setMuxCredential(null);
    setMuxAvailability("idle");
    setMuxPlayerLoading(false);
    setMuxPlaybackError("");
  }, [previewMode, course, standbyAvailability, courseId, user?.uid]);

  const beginMuxDiagnosticAttempt = useCallback(() => {
    const attempt = createMuxDiagnosticAttempt({
      functions, courseId, headerId: activeLesson?.headerId,
      sectionId: activeLesson?.sectionId, lessonId: activeLesson?.id,
      preview: previewMode,
    });
    muxDiagnosticAttemptRef.current = attempt;
    setMuxPlaybackDiagnosticId(attempt.correlationId);
    safeMuxDiagnostic(attempt, "MUX_PROVIDER_SELECTED");
    safeMuxDiagnostic(attempt, "MUX_CREDENTIAL_REQUEST_START");
    return attempt;
  }, [functions, courseId, activeLesson?.headerId, activeLesson?.sectionId,
    activeLesson?.id, previewMode]);

  const requestMuxPlaybackCredential = useCallback(async (diagnosticAttempt = null) => {
    if (!canRequestMuxPlaybackCredential({
      previewMode,
      standbyAvailability,
      accessStatus: accessReady ? accessResult?.status : "checking",
      lessonId: activeLesson?.id,
    })) return null;
    const lessonKey = [courseId, activeLesson?.headerId, activeLesson?.sectionId,
      activeLesson?.id].join("|");
    if (muxCredential?.lessonKey === lessonKey && credentialIsUsable(muxCredential)) {
      safeMuxDiagnostic(diagnosticAttempt, "MUX_CREDENTIAL_REQUEST_OK", {
        credentialSource: "memory",
      });
      return muxCredential;
    }
    try {
      setMuxAvailability("checking");
      const callable = httpsCallable(functions, "generateSignedMuxPlaybackUrl");
      const response = await callable({
        courseId,
        headerId: activeLesson.headerId,
        sectionId: activeLesson.sectionId,
        itemId: activeLesson.id,
        preview: previewMode,
      });
      const credential = {
        playbackUrl: String(response.data?.playbackUrl || ""),
        exp: Number(response.data?.exp || 0),
        lessonKey,
      };
      if (!credentialIsUsable(credential)) throw new Error("mux-playback-credential-invalid");
      safeMuxDiagnostic(diagnosticAttempt, "MUX_CREDENTIAL_REQUEST_OK", {
        credentialSource: "callable",
      });
      setMuxCredential(credential);
      setMuxAvailability("ready");
      if (!previewMode && preferredPlaybackProvider === PLAYBACK_PROVIDER.MUX) {
        setActivePlaybackProvider(PLAYBACK_PROVIDER.MUX);
      }
      return credential;
    } catch (error) {
      safeMuxDiagnostic(diagnosticAttempt, "MUX_CREDENTIAL_REQUEST_FAILED", {
        failureStage: "credential-callable",
        errorName: String(error?.name || "Error").slice(0, 80),
      });
      safeFlushMuxDiagnostic(diagnosticAttempt, "credential-failed");
      setMuxCredential(null);
      setMuxAvailability("unavailable");
      setActivePlaybackProvider(PLAYBACK_PROVIDER.CLOUDFLARE);
      if (!previewMode && error?.details?.reasonCode === "standby-policy-not-allowed") {
        setPreferredPlaybackProvider(clearLearnerProviderPreference({
          storage: window.sessionStorage, courseId, uid: user?.uid,
        }));
      }
      return null;
    }
  }, [previewMode, standbyAvailability, accessReady, accessResult?.status,
    activeLesson?.id, activeLesson?.headerId, activeLesson?.sectionId,
    courseId, functions, muxCredential, preferredPlaybackProvider, user?.uid]);

  useEffect(() => {
    if (!activeLesson?.id || (!previewMode && !learnerStandbyEnabled)) return;
    requestMuxPlaybackCredential();
  }, [previewMode, learnerStandbyEnabled, activeLesson?.id, requestMuxPlaybackCredential]);

  const [sidebarFullyLoaded, setSidebarFullyLoaded] = useState(false);
  const [sidebarLoading, setSidebarLoading] = useState(false);

  const [bootstrapLesson, setBootstrapLesson] = useState(null);

  const userTouchedSidebarRef = useRef(false);
  const autoOpenedCurrentLessonRef = useRef(false);

  const sidebarFullyLoadedRef = useRef(false);
  const flatVisibleLessonsRef = useRef([]);


  

  const [pendingReplace, setPendingReplace] = useState(null);
  const [downloadModalGroup, setDownloadModalGroup] = useState(null);
  const [pendingSummaryDownloadGroup, setPendingSummaryDownloadGroup] = useState(null);
  const [accessChecking, setAccessChecking] = useState(false);

  const [accessCheckTick, setAccessCheckTick] = useState(0);

  const [activeCourseBlocked, setActiveCourseBlocked] = useState(null);

  const [reclaimingSession, setReclaimingSession] = useState(false);

   const usableEnrolls = useMemo(() => {
    if (previewMode) return [];
    return safeArray(grantedEnrolls).filter((e) => !isInstallmentEnrollOverdue(e));
  }, [grantedEnrolls, previewMode]);

  const overdueInstallmentEnrolls = useMemo(() => {
    if (previewMode) return [];
    return safeArray(grantedEnrolls).filter((e) => isInstallmentEnrollOverdue(e));
  }, [grantedEnrolls, previewMode]);

  const allowedHeaderTypes = useMemo(() => {
    if (previewMode) return [];
    return Array.from(
      new Set(
        safeArray(usableEnrolls).flatMap((e) =>
          Array.isArray(e?.allowedHeaderTypes) ? e.allowedHeaderTypes : []
        )
      )
    );
  }, [usableEnrolls, previewMode]);

  const allowedDownloadScopes = useMemo(() => {
    if (previewMode) return ["general"];
    return Array.from(
      new Set(
        safeArray(usableEnrolls).flatMap((e) =>
          getAllowedDownloadScopesFromPackage(e?.coursePackage || "")
        )
      )
    );
  }, [usableEnrolls, previewMode]);

  useEffect(() => {
    if (previewMode) return;
    if (loadingPage) return;
    if (!courseId) return;
    if (!user?.uid) return;

    // ถ้ายังมีสิทธิ์จาก enroll อื่นอยู่ ไม่ต้อง redirect
    if (usableEnrolls.length > 0) return;

    // redirect เฉพาะเมื่อไม่มี usable enroll เหลือแล้ว
    const overdueEnroll = overdueInstallmentEnrolls[0];
    if (!overdueEnroll?.id) return;

    navigate(
      `/courses/${courseId}/payment-promptpay/${overdueEnroll.id}`,
      {
        replace: true,
        state: {
          fromOverdueInstallmentGuard: true,
        },
      }
    );
  }, [
    previewMode,
    loadingPage,
    courseId,
    user?.uid,
    usableEnrolls,
    overdueInstallmentEnrolls,
    navigate,
  ]);

  const visibleHeaders = useMemo(() => {
    if (!course) return [];
    const isThaibar = course?.courseType === "thaibar";

    return safeArray(headers)
      .filter((h) => {
        if (h?.isActive === false) return false;
        if (previewMode) return true;
        if (!isThaibar) return true;

        const headerType = String(h?.headerType || "").trim();
        if (!headerType) return true;
        return allowedHeaderTypes.includes(headerType);
      })
      .map((h) => ({
        ...h,
        sections: safeArray(h.sections).map((sec) => ({
          ...sec,
          items: safeArray(sec.items).filter((it) => canShowLesson(it, previewMode)),
        })),
      }));
  }, [headers, course, previewMode, allowedHeaderTypes]);

  const visibleCourseDownloads = useMemo(() => {
    const docs = safeArray(course?.courseDownloads);

    if (previewMode) {
      return docs.filter(
        (item) => !SPECIAL_DOWNLOAD_SCOPES.has(item?.scope) && isRealPdfFile(item)
      );
    }

    return docs.filter(
      (x) =>
        !SPECIAL_DOWNLOAD_SCOPES.has(x?.scope) &&
        isRealPdfFile(x) &&
        allowedDownloadScopes.includes(String(x?.scope || "general"))
    );
  }, [course, allowedDownloadScopes, previewMode]);

  const specialCourseDownloads = useMemo(() => {
    if (course?.courseType !== "thaibar") return { civil: null, criminal: null };
    const docs = safeArray(course?.courseDownloads);
    const civil = resolveSpecialPdf(docs, "statute_civil");
    const criminal = resolveSpecialPdf(docs, "statute_criminal");
    return {
      civil: isRealPdfFile(civil) ? civil : null,
      criminal: isRealPdfFile(criminal) ? criminal : null,
    };
  }, [course]);

  const groupedCourseDownloads = useMemo(() => {
    const map = new Map();

    visibleCourseDownloads.forEach((file) => {
      const scope = String(file?.scope || "general");

      if (!map.has(scope)) {
        map.set(scope, {
          scope,
          title: getDownloadScopeLabel(scope),
          files: [],
        });
      }

      map.get(scope).files.push(file);
    });

    return Array.from(map.values());
  }, [visibleCourseDownloads]);

  useEffect(() => {
    if (loadingPage || err || !course || !sidebarFullyLoaded) return;
    if (!previewMode && (!user?.uid || !grantedEnrolls.length ||
        (accessReady && accessResult?.status === "denied"))) return;
    lessonLoadDiagnosticRef.current?.ready();
  }, [loadingPage, err, course, sidebarFullyLoaded, previewMode, user?.uid,
    grantedEnrolls.length, accessReady, accessResult?.status]);

  const flatVisibleLessons = useMemo(() => flattenVisibleLessons(visibleHeaders), [visibleHeaders]);

  useEffect(() => {
    sidebarFullyLoadedRef.current = sidebarFullyLoaded;
  }, [sidebarFullyLoaded]);

  useEffect(() => {
    flatVisibleLessonsRef.current = flatVisibleLessons;
  }, [flatVisibleLessons]);

  const goToNextLesson = useCallback(() => {
    if (!activeLesson?.id) return false;

    const nextLesson = getNextLessonFromVisibleHeaders(
      visibleHeaders,
      activeLesson.id
    );

    if (!nextLesson) return false;

    setCurriculumExpandedAll(false);
    setOpenSidebarHeaderKey(String(nextLesson.__headerIndex ?? 0));
    setOpenSidebarSectionKey(
      `${nextLesson.__headerIndex ?? 0}-${nextLesson.__sectionIndex ?? 0}`
    );

    setActiveLesson(nextLesson);
    return true;
  }, [activeLesson?.id, visibleHeaders]);

  // Keep end-of-playback navigation fresh without making the Mux media
  // instance depend on curriculum array/callback identity churn.
  const goToNextLessonRef = useRef(goToNextLesson);
  useEffect(() => {
    goToNextLessonRef.current = goToNextLesson;
  }, [goToNextLesson]);

 

  useEffect(() => {
    if (activeLesson) {
      setPlayerLoading(true);
    }
  }, [activeLesson?.id]);

  useEffect(() => {
    if (!activeLesson?.id) return;

    playerTopRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [activeLesson?.id]);

  useEffect(() => {
    if (previewMode || !user?.uid || !courseId) {
      setLastProgress(null);
      setProgressLoaded(true);
      return;
    }

    const diagnostic = lessonLoadDiagnosticRef.current;
    (async () => {
      try {
        setProgressLoaded(false);
        const progressRef = doc(db, "users", user.uid, "courseProgress", courseId);
        const progressSnap = await observeLessonLoad(diagnostic, "PROGRESS_WAIT", () => getDoc(progressRef));
        setLastProgress(progressSnap.exists() ? progressSnap.data() : null);
      } catch (e) {
        console.error("load course progress error:", e);
        setLastProgress(null);
      } finally {
        setProgressLoaded(true);
      }
    })();
  }, [previewMode, user?.uid, courseId]);

  useEffect(() => {
  if (!visibleHeaders.length) {
    setOpenSidebarHeaderKey("");
    setOpenSidebarSectionKey("");
  }
}, [visibleHeaders]);

const loadFullCurriculum = async () => {
  if (!courseId) return;
  curriculumAttemptRef.current?.controller.abort();
  const attempt = { controller: new AbortController(), scope: curriculumScope };
  curriculumAttemptRef.current = attempt;
  const isCurrent = () => curriculumAttemptRef.current === attempt &&
    curriculumScopeRef.current === attempt.scope && !attempt.controller.signal.aborted;
  const diagnostic = lessonLoadDiagnosticRef.current;
  const totalToken = diagnostic?.begin("CURRICULUM_TOTAL");

  try {
    setSidebarLoading(true);

    const headerSnap = await observeLessonLoad(diagnostic, "CURRICULUM_HEADERS", () => getDocs(
      query(collection(db, "courses", courseId, "headers"), orderBy("order", "asc"))
    ), true);

    if (!isCurrent()) return;
    const hs = await loadCurriculumChildren({
      headers: headerSnap.docs,
      signal: attempt.controller.signal,
      readSections: headerId => observeLessonLoad(diagnostic, "CURRICULUM_SECTIONS", () => getDocs(
        query(collection(db, "courses", courseId, "headers", headerId, "sections"), orderBy("order", "asc"))
      ), true),
      readItems: (headerId, sectionId) => observeLessonLoad(diagnostic, "CURRICULUM_ITEMS", () => getDocs(
        query(collection(db, "courses", courseId, "headers", headerId, "sections", sectionId, "items"), orderBy("order", "asc"))
      ), true),
    });
    if (!isCurrent()) return;

    diagnostic?.end(totalToken);
    setHeaders(hs);
    setSidebarFullyLoaded(true);
  } catch (e) {
    if (!isCurrent()) return;
    diagnostic?.end(totalToken, e);
    diagnostic?.finish("error");
    console.error("load full curriculum error:", e);
  } finally {
    if (isCurrent()) setSidebarLoading(false);
  }
};

useEffect(() => {
  if (previewMode) return;
  if (!user?.uid) return;
  if (!courseId) return;
  if (!sidebarFullyLoaded) return;
  if (!flatVisibleLessons.length) return;

  const repairProgressSummary = async () => {
    try {
      const lessonStates = lastProgressRef.current?.lessonStates || {};
      const progressSummary = buildCourseProgressSummary(
        flatVisibleLessons,
        lessonStates
      );

      if (!progressSummary.totalLessons) return;

      const progressRef = doc(db, "users", user.uid, "courseProgress", courseId);

      await setDoc(
        progressRef,
        {
          courseId,
          ...progressSummary,
          progressSummaryUpdatedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      const nextProgress = {
        ...(lastProgressRef.current || {}),
        courseId,
        ...progressSummary,
        updatedAt: new Date(),
      };

      lastProgressRef.current = nextProgress;
      setLastProgress(nextProgress);
    } catch (e) {
      console.error("repair progress summary error:", e);
    }
  };

  repairProgressSummary();
}, [previewMode, user?.uid, courseId, sidebarFullyLoaded, flatVisibleLessons]);

  // Visibility callbacks outlive the render that installed them. Consult the
  // current scope and access state before retaining media or committing results.
  const refreshScope = [courseId, user?.uid || "", previewMode].join("|");
  const refreshContextRef = useRef(null);
  refreshContextRef.current = {
    scope: refreshScope,
    authorized: !!course && !err && !activeCourseBlocked?.blocked &&
      (previewMode || (!!user?.uid && grantedEnrolls.length > 0 && accessResult?.status === "allowed")),
  };
  const hasAuthorizedPlayer = () => refreshContextRef.current?.authorized &&
    !!(iframeRef.current?.isConnected || muxVideoRef.current?.isConnected);

  const loadCourseViewData = async ({ silent = false } = {}) => {
    if (!courseId) return;
    const diagnostic = silent ? null : lessonLoadDiagnosticRef.current;
    const requestScope = [courseId, user?.uid || "", previewMode].join("|");
    const isCurrent = () => refreshContextRef.current?.scope === requestScope;

    try {
      if (!silent) {
        setLoadingPage(true);
        setErr("");
      }

      const courseSnap = await observeLessonLoad(diagnostic, "COURSE_WAIT", () => getDoc(doc(db, "courses", courseId)));
      if (!isCurrent()) return;
      if (!courseSnap.exists()) {
        diagnostic?.finish("error");
        setErr("ไม่พบคอร์ส");
        if (!silent) setLoadingPage(false);
        return;
      }

      const courseData = { id: courseSnap.id, ...courseSnap.data() };

      /* const headerSnap = await getDocs(
        query(collection(db, "courses", courseId, "headers"), orderBy("order", "asc"))
      );

      const hs = [];

      for (const h of headerSnap.docs) {
        const header = { id: h.id, ...h.data(), sections: [] };

        const secSnap = await getDocs(
          query(
            collection(db, "courses", courseId, "headers", h.id, "sections"),
            orderBy("order", "asc")
          )
        );

        const secs = [];

        for (const s of secSnap.docs) {
          const sec = { id: s.id, ...s.data(), items: [] };

          const itemSnap = await getDocs(
            query(
              collection(db, "courses", courseId, "headers", h.id, "sections", s.id, "items"),
              orderBy("order", "asc")
            )
          );

          sec.items = itemSnap.docs.map((x) => ({
            id: x.id,
            headerId: h.id,
            sectionId: s.id,
            ...x.data(),
          }));

          secs.push(sec);
        }

        header.sections = secs;
        hs.push(header);
      }

      setHeaders(hs); */

      if (!previewMode) {
        if (!user?.uid) {
          setGrantedEnrolls([]);
        } else {
          const enrollSnap = await observeLessonLoad(diagnostic, "ENROLLMENT_WAIT", () => getDocs(
            query(
              collection(db, "enrolls"),
              where("userId", "==", user.uid),
              where("courseId", "==", courseId)
            )
          ));

          const rows = enrollSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((x) => x?.accessStatus === "granted");

          if (!isCurrent()) return;
          setGrantedEnrolls(rows);
        }
      } else {
        setGrantedEnrolls([]);
      }

      setCourse(courseData);
      setErr("");
      if (!silent) setLoadingPage(false);
    } catch (e) {
      if (!isCurrent()) return;
      diagnostic?.finish("error");
      console.error(e);
      if (!(silent && hasAuthorizedPlayer() && isTransientCourseRefreshError(e))) {
        setErr(e?.message || "โหลดคอร์สไม่สำเร็จ");
      }
      if (!silent) setLoadingPage(false);
    }
  };

  useEffect(() => {
    setHeaders([]);
    setSidebarLoading(false);
    setSidebarFullyLoaded(false);
    setBootstrapLesson(null);
    setActiveLesson(null);
    currentVideoRequestKeyRef.current = "";
    userTouchedSidebarRef.current = false;
    autoOpenedCurrentLessonRef.current = false;

    loadCourseViewData({ silent: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, previewMode, user?.uid]);

  useEffect(() => {
    if (!courseId) return;
    if (!course) return;
    if (!progressLoaded) return;
    if (sidebarFullyLoaded) return;

    loadFullCurriculum();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, course?.id, progressLoaded, sidebarFullyLoaded]);

  useEffect(() => {
    if (previewMode) return;
    if (!courseId) return;
    if (!progressLoaded) return;

    const loadBootstrapLesson = async () => {
      try {
        if (
          !lastProgress?.lastHeaderId ||
          !lastProgress?.lastSectionId ||
          !lastProgress?.lastLessonId
        ) {
          setBootstrapLesson(null);
          return;
        }

        const lessonSnap = await getDoc(
          doc(
            db,
            "courses",
            courseId,
            "headers",
            lastProgress.lastHeaderId,
            "sections",
            lastProgress.lastSectionId,
            "items",
            lastProgress.lastLessonId
          )
        );

        if (!lessonSnap.exists()) {
          setBootstrapLesson(null);
          return;
        }

        const lesson = {
          id: lessonSnap.id,
          headerId: lastProgress.lastHeaderId,
          sectionId: lastProgress.lastSectionId,
          ...lessonSnap.data(),
        };

        if (!canShowLesson(lesson, previewMode)) {
          setBootstrapLesson(null);
          return;
        }

        setBootstrapLesson(lesson);
      } catch (e) {
        console.error("load bootstrap lesson error:", e);
        setBootstrapLesson(null);
      }
    };

    loadBootstrapLesson();
  }, [courseId, progressLoaded, lastProgress, previewMode]);

  useEffect(() => {
    if (!bootstrapLesson?.id) return;

    setActiveLesson((prev) => {
      if (prev?.id) return prev;
      return bootstrapLesson;
    });
  }, [bootstrapLesson]);

  useEffect(() => {
    const refetchOnReturn = () => {
      if (previewMode) return;
      if (!courseId) return;

      // กันกรณี tab ยังไม่ visible
      if (document.visibilityState && document.visibilityState !== "visible") return;

      loadCourseViewData({ silent: true });

      // ✅ เพิ่มอันนี้: กลับมาที่ tab แล้วตรวจอุปกรณ์ใหม่
      setAccessCheckTick((x) => x + 1);
    };

    
    document.addEventListener("visibilitychange", refetchOnReturn);

    return () => {
      
      document.removeEventListener("visibilitychange", refetchOnReturn);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, previewMode, user?.uid]);

 useEffect(() => {
  if (!progressLoaded) return;
  if (!flatVisibleLessons.length) return;

  const firstLesson = flatVisibleLessons[0] || null;

  const resumedLesson = lastProgress?.lastLessonId
    ? flatVisibleLessons.find((it) => it.id === lastProgress.lastLessonId)
    : null;

  setActiveLesson((prev) => {
    const currentSynced = prev?.id
      ? flatVisibleLessons.find((it) => it.id === prev.id)
      : null;

    const nextLesson = currentSynced || resumedLesson || firstLesson || null;

    if (
      nextLesson &&
      !userTouchedSidebarRef.current &&
      !autoOpenedCurrentLessonRef.current
    ) {
      setCurriculumExpandedAll(false);
      setOpenSidebarHeaderKey(String(nextLesson.__headerIndex ?? 0));
      setOpenSidebarSectionKey(
        `${nextLesson.__headerIndex ?? 0}-${nextLesson.__sectionIndex ?? 0}`
      );
      autoOpenedCurrentLessonRef.current = true;
    }

    return nextLesson;
  });
}, [flatVisibleLessons, lastProgress, progressLoaded]);



  useEffect(() => {
    if (previewMode) {
      setAccessReady(true);
      setAccessResult({
        status: "allowed",
        ok: true,
        message: "",
        watermarkText: "",
        settings: null,
      });
      setPendingReplace(null);
      return;
    }

    if (loadingPage) return;

    if (!user?.uid) {
      setAccessReady(true);
      setAccessResult({
        status: "denied",
        ok: false,
        message: "กรุณาเข้าสู่ระบบก่อน",
        watermarkText: "",
        settings: null,
      });
      return;
    }

    if (!grantedEnrolls.length) {
      setAccessReady(true);
      setAccessResult((prev) => ({ ...prev, status: "idle", ok: false }));
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setAccessChecking(true);
        setAccessReady(false);

        const result = await runCourseAccessCheck({
          user,
          userDoc,
          courseId,
          previewMode,
        });

        if (cancelled) return;
        setAccessResult(result);

        if (result?.status === "confirm_replace") {
          setPendingReplace(result);
        } else {
          setPendingReplace(null);
        }
      } catch (e) {
        if (cancelled) return;
        if (hasAuthorizedPlayer() && isTransientCourseRefreshError(e)) return;
        setAccessResult({
          status: "denied",
          ok: false,
          message: e?.message || "ตรวจสอบการเข้าใช้งานไม่สำเร็จ",
          watermarkText: "",
          settings: null,
        });
        setPendingReplace(null);
      } finally {
        if (!cancelled) {
          setAccessChecking(false);
          setAccessReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    previewMode,
    loadingPage,
    user?.uid,
    userDoc,
    courseId,
    grantedEnrolls.length,
    accessCheckTick,
  ]);

  useEffect(() => {
    if (previewMode) return;
    if (!user?.uid) return;
    if (!courseId) return;
    if (!accessReady) return;
    if (accessResult?.status !== "allowed") return;

    let cancelled = false;
    let unsubscribe = null;
    let timer = null;
    let mySignature = "";
    let myDevice = null;

    const blockThisDevice = (current) => {
      setIframeUrl("");
      setPlayerErr("");
      setPlayerLoading(false);
      currentVideoRequestKeyRef.current = "";

      setActiveCourseBlocked({
        blocked: true,
        current,
        message:
          "บัญชีนี้กำลังใช้งานหน้าเรียนบนอุปกรณ์อื่นอยู่ จึงไม่สามารถดูวิดีโอบนอุปกรณ์นี้ได้ในขณะนี้",
      });
    };

    const start = async () => {
      try {
        const claimed = await claimCourseActiveDevice({
          user,
          userDoc,
          courseId,
          lessonId: activeLessonIdRef.current,
          forceClaim: true,
        });

        if (cancelled || !claimed?.enabled) return;

        mySignature = claimed?.device?.signature || "";
        myDevice = claimed?.device || null;

        unsubscribe = subscribeCourseActiveDevice({
          uid: user.uid,
          onChange: (current) => {
            if (cancelled) return;
            if (!current?.deviceSignature || !mySignature) return;

            if (String(current?.uid || "") !== String(user.uid)) return;
            if (String(current?.courseId || "") !== String(courseId)) return;

            if (String(current.deviceSignature) !== String(mySignature)) {
              blockThisDevice(current);
            } else {
              setActiveCourseBlocked(null);
            }
          },
        });

        const heartbeatMs =
          Math.max(
            15,
            Number(claimed?.settings?.courseActiveHeartbeatSeconds || 180)
          ) * 1000;

        timer = window.setInterval(async () => {
          try {
            const hb = await heartbeatCourseActiveDevice({
              user,
              userDoc,
              courseId,
              lessonId: activeLessonIdRef.current,
              deviceOverride: myDevice,
            });

            if (hb?.reason === "active_on_other_device") {
              blockThisDevice(hb.current);
            }
          } catch (e) {
            console.warn("course active heartbeat failed:", e?.message);
          }
        }, heartbeatMs);
      } catch (e) {
        console.warn("course active device failed:", e?.message);
      }
    };

    start();

    return () => {
      cancelled = true;
      if (typeof unsubscribe === "function") unsubscribe();
      if (timer) window.clearInterval(timer);
    };
  }, [
    previewMode,
    user?.uid,
    userDoc,
    courseId,
    accessReady,
    accessResult?.status,
  ]);

  useEffect(() => {
    if (previewMode || !user?.uid || !courseId || !activeLesson?.id) return;

    const saveLessonOnly = async () => {
      try {
        const progressRef = doc(db, "users", user.uid, "courseProgress", courseId);

        await setDoc(
          progressRef,
          {
            courseId,
            lastLessonId: activeLesson.id,
            lastHeaderId: activeLesson.headerId || "",
            lastSectionId: activeLesson.sectionId || "",
            updatedAt: serverTimestamp(),
            lastOpenedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (e) {
        console.error("save lesson progress error:", e);
      }
    };

    saveLessonOnly();
  }, [
    previewMode,
    user?.uid,
    courseId,
    activeLesson?.id,
    activeLesson?.headerId,
    activeLesson?.sectionId,
  ]);

  useEffect(() => {
    if (!activeLesson) {
      setIframeUrl("");
      setPlayerErr("");
      return;
    }

    if (!previewMode && !user) {
      setIframeUrl("");
      setPlayerErr("");
      return;
    }

    if (!previewMode && !accessReady) {
      return;
    }

    if (!previewMode && accessResult?.status !== "allowed") {
      setIframeUrl("");
      setPlayerErr("");
      return;
    }

    const videoRequestKey = [
      courseId,
      activeLesson.id,
      activeLesson.headerId,
      activeLesson.sectionId,
      user?.uid || "preview",
      previewMode ? "preview" : "normal",
    ].join("|");

    if (currentVideoRequestKeyRef.current === videoRequestKey && iframeUrl) {
      return;
    }

    currentVideoRequestKeyRef.current = videoRequestKey;

    let cancelled = false;

    (async () => {
      try {
        setPlayerLoading(true);
        setPlayerErr("");
        cloudflareSourceLessonRef.current = "";
        setIframeUrl("");
        setPlayerFrameLoaded(false);
        setShowVideoHelp(false);
        setPlayerKey((k) => k + 1);
        lastSavedTimeRef.current = 0;

        const callable = httpsCallable(functions, "generateSignedPlaybackUrl");
        const res = await callable({
          courseId,
          headerId: activeLesson.headerId,
          sectionId: activeLesson.sectionId,
          itemId: activeLesson.id,
          preview: previewMode,
        });

        if (cancelled) return;
        cloudflareSourceLessonRef.current = videoRequestKey;
        cloudflareCredentialExpRef.current = res?.data?.exp;
        setIframeUrl(res?.data?.iframeUrl || "");
        setPlayerLoading(false);
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        setPlayerErr(e?.message || "โหลดวิดีโอไม่สำเร็จ");
        setPlayerLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    activeLesson?.id,
    activeLesson?.headerId,
    activeLesson?.sectionId,
    user?.uid,
    functions,
    courseId,
    previewMode,
    accessReady,
    accessResult?.status,
    iframeUrl,
  ]);

  const runtimeIdentityRef = useRef(null);
  runtimeIdentityRef.current = {
    provider: activePlaybackProvider,
    lesson: [courseId, activeLesson?.headerId, activeLesson?.sectionId, activeLesson?.id].join("|"),
    source: activePlaybackProvider === PLAYBACK_PROVIDER.MUX ? muxCredential?.playbackUrl : iframeUrl,
    generation: [playerKey, previewMode, user?.uid, activeLesson?.video?.durationSec].join("|"),
  };

  useEffect(() => {
    if (activePlaybackProvider !== PLAYBACK_PROVIDER.CLOUDFLARE) return;
    if (!iframeUrl || !cloudflareIframe?.isConnected || !window.Stream) return;
    if (!previewMode && !user?.uid) return;
    if (!courseId || !activeLesson?.id) return;
    const lessonIdentity = [courseId, activeLesson.id, activeLesson.headerId,
      activeLesson.sectionId, user?.uid || "preview", previewMode ? "preview" : "normal"].join("|");
    if (cloudflareSourceLessonRef.current !== lessonIdentity) return;

    let cancelled = false;
    let player;
    let onTimeUpdate;
    let onEnded;
    let runtime;
    const runtimeIdentity = runtimeIdentityRef.current;
    try {
      player = window.Stream(cloudflareIframe);
      playerRef.current = player;
      const progressRef = previewMode ? null : doc(db, "users", user?.uid, "courseProgress", courseId);

      const saveProgress = async (timeSec, forceCompleted = null) => {
        if (cancelled || previewMode) return;
        const rounded = Math.max(0, Math.floor(Number(timeSec || 0)));
        if (!Number.isFinite(rounded)) return;

        const durationSec = Number(cloudflareLessonRef.current?.video?.durationSec || 0);
        const autoCompleted =
          durationSec > 0 ? rounded >= Math.max(durationSec - 15, durationSec * 0.95) : false;

        const completed =
          typeof forceCompleted === "boolean" ? forceCompleted : autoCompleted;

        if (!completed && Math.abs(rounded - lastSavedTimeRef.current) < 10) return;
        lastSavedTimeRef.current = rounded;

        const prevLessonStates = lastProgressRef.current?.lessonStates || {};

        const nextLessonStates = {
          ...prevLessonStates,
          [activeLesson.id]: {
            lastVideoTimeSec: completed ? 0 : rounded,
            completed,
            updatedAtMs: Date.now(),
          },
        };

        const currentFlatLessons = flatVisibleLessonsRef.current || [];

        const canBuildFullProgressSummary =
          sidebarFullyLoadedRef.current && currentFlatLessons.length > 0;

        const progressSummary = canBuildFullProgressSummary
          ? buildCourseProgressSummary(currentFlatLessons, nextLessonStates)
          : null;

        const progressSummaryPatch = progressSummary
          ? {
              ...progressSummary,
              progressSummaryUpdatedAt: serverTimestamp(),
            }
          : {};

        try {
          await setDoc(
            progressRef,
            {
              courseId,
              lastLessonId: activeLesson.id,
              lastHeaderId: activeLesson.headerId || "",
              lastSectionId: activeLesson.sectionId || "",
              lastVideoTimeSec: completed ? 0 : rounded,

              lessonStates: nextLessonStates,

              ...progressSummaryPatch,

              updatedAt: serverTimestamp(),
              lastOpenedAt: serverTimestamp(),
            },
            { merge: true }
          );

          if (cancelled) return;
          const nextProgress = {
            ...(lastProgressRef.current || {}),
            courseId,
            lastLessonId: activeLesson.id,
            lastHeaderId: activeLesson.headerId || "",
            lastSectionId: activeLesson.sectionId || "",
            lastVideoTimeSec: completed ? 0 : rounded,
            lessonStates: nextLessonStates,
            updatedAt: new Date(),
            lastOpenedAt: new Date(),
            ...(progressSummary || {}),
          };

          lastProgressRef.current = nextProgress;
          setLastProgress(nextProgress);
        } catch (e) {
          console.error("save video progress error:", e);
        }
      };


      onTimeUpdate = async () => {
        if (cancelled) return;
        try {
          const current = await player.currentTime;
          if (!cancelled) saveProgress(current, false);
        } catch {}
      };
      onEnded = async () => {
        if (cancelled) return;
        try {
          const durationSec = Number(cloudflareLessonRef.current?.video?.durationSec || 0);
          await saveProgress(durationSec || 0, true);
        } finally {
          if (!cancelled) goToNextLessonRef.current();
        }
      };
      if (!previewMode) {
        player.addEventListener("timeupdate", onTimeUpdate);
        player.addEventListener("ended", onEnded);
      }

      try {
        runtime = attachPlaybackRuntimeDiagnostics({ media: player, provider: "cloudflare",
          engine: "cloudflare-sdk", functions, credentialExp: cloudflareCredentialExpRef.current });
      } catch {}

      const previous = cloudflareRestoreRef.current;
      if (previous?.iframe !== cloudflareIframe || previous?.url !== iframeUrl ||
          previous?.lessonIdentity !== lessonIdentity || previous?.playerKey !== playerKey) {
        cloudflareRestoreRef.current = { iframe: cloudflareIframe, url: iframeUrl, lessonIdentity, playerKey };
        const pending = pendingProviderPositionRef.current;
        const resumeTimeSec = Number(pending.time > 0 ? pending.time :
          (previewMode ? 0 : lastProgressRef.current?.lessonStates?.[activeLesson.id]?.lastVideoTimeSec || 0));
        pendingProviderPositionRef.current = { time: 0, shouldPlay: false };
        if (resumeTimeSec > 0 && Number.isFinite(resumeTimeSec)) {
          try { player.currentTime = resumeTimeSec; } catch {}
        }
        if (pending.shouldPlay) {
          try { Promise.resolve(player.play()).catch(() => {}); } catch {}
        }
      }
    } catch (e) {
      console.error("init cloudflare stream player error:", e);
    }

    return () => {
      cancelled = true;
      try { runtime?.close(runtimeEndReason(runtimeIdentity, runtimeIdentityRef.current)); } catch {}
      if (player) {
        try { player.removeEventListener("timeupdate", onTimeUpdate); } catch {}
        try { player.removeEventListener("ended", onEnded); } catch {}
      }
      if (playerRef.current === player) playerRef.current = null;
    };
    // Object identity and navigation changes are not playback identity changes.
    // The latest lesson duration and navigation callback are read through refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlaybackProvider, iframeUrl, cloudflareIframe, playerKey, previewMode,
    user?.uid, courseId, activeLesson?.id, activeLesson?.headerId, activeLesson?.sectionId]);

  const saveMuxLearnerProgress = useCallback(async (timeSec, forceCompleted = null) => {
    if (previewMode || !user?.uid || !courseId || !activeLesson?.id) return;
    const rounded = Math.max(0, Math.floor(Number(timeSec || 0)));
    if (!Number.isFinite(rounded)) return;
    const durationSec = Number(activeLesson?.video?.durationSec || 0);
    const autoCompleted = durationSec > 0
      ? rounded >= Math.max(durationSec - 15, durationSec * 0.95) : false;
    const completed = typeof forceCompleted === "boolean" ? forceCompleted : autoCompleted;
    if (!completed && Math.abs(rounded - lastSavedTimeRef.current) < 10) return;
    lastSavedTimeRef.current = rounded;
    const nextLessonStates = {
      ...(lastProgressRef.current?.lessonStates || {}),
      [activeLesson.id]: {
        lastVideoTimeSec: completed ? 0 : rounded,
        completed,
        updatedAtMs: Date.now(),
      },
    };
    const currentFlatLessons = flatVisibleLessonsRef.current || [];
    const progressSummary = sidebarFullyLoadedRef.current && currentFlatLessons.length
      ? buildCourseProgressSummary(currentFlatLessons, nextLessonStates) : null;
    const patch = {
      courseId,
      lastLessonId: activeLesson.id,
      lastHeaderId: activeLesson.headerId || "",
      lastSectionId: activeLesson.sectionId || "",
      lastVideoTimeSec: completed ? 0 : rounded,
      lessonStates: nextLessonStates,
      ...(progressSummary ? { ...progressSummary, progressSummaryUpdatedAt: serverTimestamp() } : {}),
      updatedAt: serverTimestamp(),
      lastOpenedAt: serverTimestamp(),
    };
    try {
      await setDoc(doc(db, "users", user.uid, "courseProgress", courseId), patch, { merge: true });
      const nextProgress = {
        ...(lastProgressRef.current || {}), ...patch,
        updatedAt: new Date(), lastOpenedAt: new Date(), ...(progressSummary || {}),
      };
      lastProgressRef.current = nextProgress;
      setLastProgress(nextProgress);
    } catch (e) {
      console.error("save video progress error:", e);
    }
  }, [previewMode, user?.uid, courseId, activeLesson?.id, activeLesson?.headerId,
    activeLesson?.sectionId, activeLesson?.video?.durationSec]);

  useEffect(() => {
    if (activePlaybackProvider !== PLAYBACK_PROVIDER.MUX) return undefined;
    const video = muxVideoRef.current;
    const playbackUrl = muxCredential?.playbackUrl;
    if (!video || !playbackUrl) return undefined;
    let hls = null;
    let cancelled = false;
    let diagnosticAttempt = muxDiagnosticAttemptRef.current;
    if (!diagnosticAttempt) {
      diagnosticAttempt = beginMuxDiagnosticAttempt();
      safeMuxDiagnostic(diagnosticAttempt, "MUX_CREDENTIAL_REQUEST_OK", {
        credentialSource: "memory",
      });
    }
    let nativeErrorHandler = null;
    let runtime;
    const runtimeIdentity = runtimeIdentityRef.current;
    setMuxPlayerLoading(true);
    setMuxPlaybackError("");

    const restorePosition = async () => {
      if (cancelled) return;
      if (isMuxPlaybackStartupSuccessEvent("loadedmetadata")) setMuxPlaybackError("");
      safeMuxDiagnostic(diagnosticAttempt, "MUX_LOADED_METADATA", {}, video);
      const pending = pendingProviderPositionRef.current;
      const resumeTime = pending.time > 0 ? pending.time : Number(
        lastProgressRef.current?.lessonStates?.[activeLesson?.id]?.lastVideoTimeSec || 0
      );
      if (resumeTime > 0 && Number.isFinite(resumeTime)) video.currentTime = resumeTime;
      if (pending.shouldPlay) {
        try { await video.play(); } catch {}
      }
      pendingProviderPositionRef.current = { time: 0, shouldPlay: false };
      setMuxPlayerLoading(false);
    };
    const onPlaying = () => {
      if (cancelled) return;
      if (isMuxPlaybackStartupSuccessEvent("playing")) setMuxPlaybackError("");
      safeMuxDiagnostic(diagnosticAttempt, "MUX_PLAYING", {}, video);
      safeFlushMuxDiagnostic(diagnosticAttempt, "playing");
      setMuxPlayerLoading(false);
    };
    const onPlay = () => safeMuxDiagnostic(diagnosticAttempt, "MUX_FIRST_PLAY", {}, video);
    const fail = (details = {}) => {
      if (cancelled) return;
      safeMuxDiagnostic(diagnosticAttempt, "MUX_FATAL_ERROR", details, video);
      safeFlushMuxDiagnostic(diagnosticAttempt, "fatal-error");
      setMuxPlayerLoading(false);
      setMuxPlaybackError("ไม่สามารถเล่นวิดีโอจากระบบ Streaming สำรองได้");
    };
    const onTimeUpdate = () => saveMuxLearnerProgress(video.currentTime, false);
    const onEnded = async () => {
      await saveMuxLearnerProgress(video.duration || activeLesson?.video?.durationSec || 0, true);
      goToNextLessonRef.current();
    };
    video.addEventListener("loadedmetadata", restorePosition);
    video.addEventListener("play", onPlay, { once: true });
    video.addEventListener("playing", onPlaying);
    if (!previewMode) {
      video.addEventListener("timeupdate", onTimeUpdate);
      video.addEventListener("ended", onEnded);
    }

    const nativeHlsSupported = !!video.canPlayType("application/vnd.apple.mpegurl");
    const mediaSourceSupported = typeof window.MediaSource !== "undefined";
    const playbackEnvironment = detectMuxPlaybackEnvironment(navigator.userAgent);
    const preferHlsJs = shouldProbeHlsJsFirst(playbackEnvironment);

    const startNativeHls = () => {
      if (cancelled) return;
      try {
        runtime = attachPlaybackRuntimeDiagnostics({ media: video, provider: "mux", engine: "native-hls",
          functions, credentialExp: muxCredential?.exp });
        muxRuntimeRef.current = { observer: runtime, identity: runtimeIdentity };
      } catch {}
      safeMuxDiagnostic(diagnosticAttempt, "MUX_NATIVE_HLS_ENGINE_SELECTED", {
        playbackEngine: "native-hls",
        mediaSourceSupported,
      }, video);
      nativeErrorHandler = () => fail({
        playbackEngine: "native-hls",
        failureStage: "native-media-error",
        mediaErrorCode: Number(video.error?.code || 0),
      });
      video.addEventListener("error", nativeErrorHandler);
      video.src = playbackUrl;
      video.load();
    };

    const loadHlsJs = () => {
      import("hls.js").then(({ default: Hls }) => {
        if (cancelled) return;
        const hlsSupported = Hls.isSupported();
        const selectedEngine = selectMuxPlaybackEngine({
          preferHlsJs,
          nativeHlsSupported,
          mediaSourceSupported,
          hlsJsSupported: hlsSupported,
        });
        if (selectedEngine === MUX_PLAYBACK_ENGINE.NATIVE_HLS) {
          startNativeHls();
          return;
        }
        safeMuxDiagnostic(diagnosticAttempt, "MUX_HLS_ENGINE_SELECTED", {
          playbackEngine: "hls.js", hlsSupported,
          mediaSourceSupported,
        }, video);
        if (selectedEngine === MUX_PLAYBACK_ENGINE.UNSUPPORTED) {
          fail({ playbackEngine: "hls.js", hlsSupported: false,
            failureStage: "hls-unsupported" });
          return;
        }
        hls = new Hls({ enableWorker: true });
        try {
          runtime = attachPlaybackRuntimeDiagnostics({ media: video, provider: "mux", engine: "hls.js",
            functions, credentialExp: muxCredential?.exp });
          muxRuntimeRef.current = { observer: runtime, identity: runtimeIdentity };
        } catch {}
        hls.on(Hls.Events.ERROR, (_event, data) => {
          try { runtime?.error(data); } catch {}
          if (shouldShowHlsJsPlaybackFailure(data)) fail({
            playbackEngine: "hls.js", ...sanitizeHlsFatalError(data),
          });
        });
        hls.once(Hls.Events.MEDIA_ATTACHED, () =>
          safeMuxDiagnostic(diagnosticAttempt, "MUX_MEDIA_ATTACHED", {}, video));
        hls.once(Hls.Events.MANIFEST_LOADING, () =>
          safeMuxDiagnostic(diagnosticAttempt, "MUX_MANIFEST_LOADING", {}, video));
        hls.once(Hls.Events.MANIFEST_LOADED, (_event, data) =>
          safeMuxDiagnostic(diagnosticAttempt, "MUX_MANIFEST_LOADED",
            codecDiagnostics(data?.levels), video));
        hls.once(Hls.Events.LEVEL_LOADED, () =>
          safeMuxDiagnostic(diagnosticAttempt, "MUX_LEVEL_LOADED", {}, video));
        hls.once(Hls.Events.FRAG_LOADED, () =>
          safeMuxDiagnostic(diagnosticAttempt, "MUX_FIRST_FRAGMENT_LOADED", {}, video));
        hls.once(Hls.Events.BUFFER_APPENDED, () =>
          safeMuxDiagnostic(diagnosticAttempt, "MUX_FIRST_BUFFER_APPENDED", {}, video));
        hls.loadSource(playbackUrl);
        hls.attachMedia(video);
      }).catch((error) => {
        if (nativeHlsSupported) {
          startNativeHls();
          return;
        }
        fail({ playbackEngine: "hls.js", failureStage: "hls-import-or-setup",
          errorName: String(error?.name || "Error").slice(0, 80) });
      });
    };

    if (preferHlsJs || !nativeHlsSupported) {
      loadHlsJs();
    } else {
      startNativeHls();
    }

    return () => {
      cancelled = true;
      try { runtime?.close(runtimeEndReason(runtimeIdentity, runtimeIdentityRef.current)); } catch {}
      if (muxRuntimeRef.current?.observer === runtime) muxRuntimeRef.current = null;
      video.removeEventListener("loadedmetadata", restorePosition);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("playing", onPlaying);
      if (nativeErrorHandler) video.removeEventListener("error", nativeErrorHandler);
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("ended", onEnded);
      if (hls) hls.destroy();
      video.pause();
      video.removeAttribute("src");
      video.load();
    };
    // Diagnostics capture this generation's expiry; metadata-only changes must not restart the 6.7E player.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewMode, activePlaybackProvider, muxCredential?.playbackUrl,
    activeLesson?.id, activeLesson?.video?.durationSec, saveMuxLearnerProgress,
    beginMuxDiagnosticAttempt]);

  const capturePreviewPosition = async () => {
    if (activePlaybackProvider === PLAYBACK_PROVIDER.MUX && muxVideoRef.current) {
      return { time: Number(muxVideoRef.current.currentTime || 0),
        shouldPlay: !muxVideoRef.current.paused };
    }
    const player = playerRef.current;
    if (!player) return { time: 0, shouldPlay: false };
    try {
      const time = Number(await player.currentTime || 0);
      const paused = typeof player.paused === "boolean" ? player.paused : await player.paused;
      return { time, shouldPlay: paused === false };
    } catch {
      return { time: 0, shouldPlay: false };
    }
  };

  const switchToMuxPlayback = async () => {
    const maySwitch = previewMode
      ? canOfferMuxPreview({ previewMode, muxAvailability })
      : learnerStandbyEnabled && accessResult?.status === "allowed" && muxAvailability === "ready";
    if (!maySwitch) return;
    const diagnosticAttempt = beginMuxDiagnosticAttempt();
    const credential = credentialIsUsable(muxCredential)
      ? (safeMuxDiagnostic(diagnosticAttempt, "MUX_CREDENTIAL_REQUEST_OK", {
        credentialSource: "memory",
      }), muxCredential) : await requestMuxPlaybackCredential(diagnosticAttempt);
    if (!credential) return;
    pendingProviderPositionRef.current = await capturePreviewPosition();
    setMuxPlaybackError("");
    if (previewMode) {
      setActivePlaybackProvider(switchPreviewProvider({
        previewMode, muxAvailability: "ready", provider: PLAYBACK_PROVIDER.MUX,
      }));
    } else {
      const preferred = writeLearnerProviderPreference({
        storage: window.sessionStorage, courseId, uid: user?.uid,
        provider: PLAYBACK_PROVIDER.MUX,
      });
      setPreferredPlaybackProvider(preferred);
      setActivePlaybackProvider(resolveActivePlaybackProvider({
        preferredProvider: preferred, muxAvailability: "ready",
      }));
    }
  };

  const switchToCloudflarePlayback = async () => {
    pendingProviderPositionRef.current = await capturePreviewPosition();
    if (!previewMode) {
      setPreferredPlaybackProvider(writeLearnerProviderPreference({
        storage: window.sessionStorage, courseId, uid: user?.uid,
        provider: PLAYBACK_PROVIDER.CLOUDFLARE,
      }));
    }
    setActivePlaybackProvider(PLAYBACK_PROVIDER.CLOUDFLARE);
    setMuxPlaybackError("");
    setPlayerLoading(true);
    setPlayerFrameLoaded(false);
    setPlayerKey((key) => key + 1);
  };

  useEffect(() => {
    if (videoHelpTimerRef.current) {
      clearTimeout(videoHelpTimerRef.current);
    }

    setShowVideoHelp(false);

    if (!activeLesson?.id || !iframeUrl || playerErr) return;

    videoHelpTimerRef.current = setTimeout(() => {
      if (!playerFrameLoaded) {
        setShowVideoHelp(true);
      }
    }, 10000);

    return () => {
      if (videoHelpTimerRef.current) {
        clearTimeout(videoHelpTimerRef.current);
      }
    };
  }, [activeLesson?.id, iframeUrl, playerErr, playerKey, playerFrameLoaded]);

  if (loadingPage) {
    return (
      <div className="container py-5 text-center text-secondary">
        <Spinner animation="border" size="sm" /> กำลังโหลดคอร์ส...
      </div>
    );
  }

  if (err) {
    return (
      <div className="container py-4 courseview-page">
        <Alert variant="danger">{err}</Alert>
        <Link to="/courses">
          <Button variant="outline-light">กลับหน้าคอร์ส</Button>
        </Link>
      </div>
    );
  }

 if (!course) return null;

// ✅ เพิ่มใหม่: ยังไม่ login
if (!previewMode && !user?.uid) {
  return (
    <div className="container py-4 courseview-page">
      <Alert variant="warning" className="mb-3">
        กรุณาเข้าสู่ระบบก่อนเพื่อเข้าดูคอร์สนี้
      </Alert>

      <div className="d-flex gap-2 flex-wrap">
        <Link
          to="/login"
          state={{ from: { pathname: `/learn/${courseId}` } }}
        >
          <Button variant="primary">ไปหน้าเข้าสู่ระบบ</Button>
        </Link>

        <Link to={`/courses/${course.slug || course.id}`}>
          <Button variant="outline-light">กลับหน้าคอร์ส</Button>
        </Link>
      </div>
    </div>
  );
}

// ✅ เดิม: login แล้วแต่ไม่มีสิทธิ์
if (!previewMode && !grantedEnrolls.length) {
  return (
    <div className="container py-4 courseview-page">
      <Alert variant="warning" className="mb-3">
        คุณยังไม่มีสิทธิเข้าถึงคอร์สนี้
      </Alert>
      <Link to={`/courses/${course.slug || course.id}`}>
        <Button variant="outline-light">กลับหน้าคอร์ส</Button>
      </Link>
    </div>
  );
}

// ✅ สำคัญ: access control (device limit / blocked)
if (!previewMode && accessReady && accessResult?.status === "denied") {
  return (
    <div className="container py-4 courseview-page">
      <Alert variant="warning" className="mb-3">
        {accessResult?.message || "ไม่สามารถเข้าเรียนได้"}
      </Alert>
      <Link to={`/courses/${course.slug || course.id}`}>
        <Button variant="outline-light">กลับหน้าคอร์ส</Button>
      </Link>
    </div>
  );
}

const reclaimCourseSession = async () => {
  try {
    setReclaimingSession(true);

    await claimCourseActiveDevice({
      user,
      userDoc,
      courseId,
      lessonId: activeLesson?.id,
      forceClaim: true,
    });

    setActiveCourseBlocked(null);
  } catch (e) {
    console.error(e);
  } finally {
    setReclaimingSession(false);
  }
};

if (!previewMode && activeCourseBlocked?.blocked) {
  return (
    <div className="container py-4 courseview-page">
      <Modal show centered backdrop="static" keyboard={false}>
        <Modal.Header>
          <Modal.Title>มีการใช้งานบนอุปกรณ์อื่น</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="mb-2">{activeCourseBlocked.message}</p>
          <p className="text-secondary small mb-0">
            หากต้องการเรียนบนอุปกรณ์นี้ กรุณาปิดหน้าเรียนบนอุปกรณ์อื่น
            หรือเข้าใช้งานใหม่อีกครั้งภายหลัง
            <br/><br/>
            การกลับเข้าเรียนจะย้ายการใช้งานมายังอุปกรณ์นี้
            และหยุดการใช้งานบนอุปกรณ์เดิม
          </p>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="success"
            onClick={reclaimCourseSession}
            disabled={reclaimingSession}
          >
            {reclaimingSession ? "กำลังกลับเข้าเรียน..." : "กลับเข้าเรียน"}
          </Button>

          <Button
            variant="secondary"
            onClick={() => navigate("/")}
          >
            กลับหน้าแรก
          </Button>
        </Modal.Footer>
      </Modal>

      <Alert variant="warning">
        บัญชีนี้กำลังใช้งานหน้าเรียนบนอุปกรณ์อื่นอยู่
      </Alert>
    </div>
  );
}

const toggleExpandAllCurriculum = () => {
  userTouchedSidebarRef.current = true;
  if (curriculumExpandedAll) {
    setOpenSidebarHeaderKey("");
    setOpenSidebarSectionKey("");
    setCurriculumExpandedAll(false);
    return;
  }

  setOpenSidebarHeaderKey("__all__");
  setOpenSidebarSectionKey("__all__");
  setCurriculumExpandedAll(true);
};

const renderCourseDownloads = () => {
  if (groupedCourseDownloads.length === 0) return null;

  return (
    <div className="mt-4">
      <div className="fw-semibold mb-2">เอกสารดาวน์โหลดของคอร์ส</div>

      <div className="d-flex flex-column gap-2">
        {groupedCourseDownloads.map((group) => (
          <div
            key={group.scope}
            className="courseview-file-box d-flex align-items-center justify-content-between gap-3"
          >
            <div className="min-w-0">
              <div className="fw-semibold text-break">{group.title}</div>
              <div className="courseview-file-meta">
                PDF · {group.files.length} ไฟล์
              </div>
            </div>

            <Button
              size="sm"
              variant="outline-light"
              onClick={() => {
                if (group.scope === "summary_civil" || group.scope === "summary_criminal") {
                  setPendingSummaryDownloadGroup(group);
                } else {
                  setDownloadModalGroup(group);
                }
              }}
            >
              ดาวน์โหลด
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

const scheduleImages = safeArray(course?.courseScheduleImages).filter(
  (img) => img?.imageUrl && img?.isActive !== false
);

const calendarImages = safeArray(course?.courseCalendarImages).filter(
  (img) => img?.imageUrl && img?.isActive !== false
);

const checklistImages = safeArray(course?.courseChecklistImages).filter(
  (img) => img?.imageUrl && img?.isActive !== false
);

const lineGroupQrImage = course?.lineGroupQrImage || {};
const lineGroupQrImageUrl =
  lineGroupQrImage?.isActive !== false
    ? String(lineGroupQrImage?.imageUrl || "").trim()
    : "";


    const reloadVideoPlayer = () => {
      setShowVideoHelp(false);
      setPlayerLoading(true);
      setPlayerKey((k) => k + 1);

      window.setTimeout(() => {
        setPlayerLoading(false);
      }, 1500);
    };


  return (
    <div className="container py-4 courseview-page">
      <div className="mb-3">
        <h3 className="mb-1">{course.title}</h3>

        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="text-secondary small">
            {course.subTitle || ""}
          </div>

          <div className="d-flex align-items-center gap-3 flex-wrap">
            {lineGroupQrImageUrl ? (
              <button
                type="button"
                className="btn btn-link p-0 text-primary text-decoration-none small"
                onClick={() => setShowQrModal(true)}
              >
                QR กลุ่มติว
              </button>
            ) : null}

            {previewMode ? (
              <Badge bg="warning" text="dark">
                Preview Mode
              </Badge>
            ) : null}

            <Button
              variant="outline-light"
              onClick={toggleExpandAllCurriculum}
              className="d-none d-lg-inline-flex"
            >
              {curriculumExpandedAll ? "ย่อทั้งหมด" : "แสดงทั้งหมด"}
            </Button>
          </div>
        </div>
      </div>

      <Row className="g-3">
        <Col lg={8}>
        <div ref={playerTopRef}>
          <Card className="courseview-card">
            <Card.Body>
              <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                {course?.showCourseScheduleImages !== false && scheduleImages.length > 0 ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-light courseview-top-action-btn"
                    onClick={() => {
                      setSchedulePreviewIndex(0);
                      setShowScheduleModal(true);
                    }}
                  >
                    <BsCalendar2Heart className="me-1" />
                    ตารางเรียน
                  </button>
                ) : null}

                {course?.showCourseCalendarImages !== false && calendarImages.length > 0 ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-light courseview-top-action-btn"
                    onClick={() => {
                      setCalendarPreviewIndex(0);
                      setShowCalendarModal(true);
                    }}
                  >
                    <BsCalendar2Event className="me-1" />
                    ปฏิทิน
                  </button>
                ) : null}

                {course?.showCourseChecklistImages !== false && checklistImages.length > 0 ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-light courseview-top-action-btn"
                    onClick={() => {
                      setChecklistPreviewIndex(0);
                      setShowChecklistModal(true);
                    }}
                  >
                    <BsCalendar2Check className="me-1" />
                    Checklist
                  </button>
                ) : null}

                {specialCourseDownloads.civil ? (
                  <a
                    href={specialCourseDownloads.civil.fileUrl || specialCourseDownloads.civil.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="เปิดลายแทงมาตรา แพ่ง"
                    className="btn btn-sm btn-outline-light courseview-top-action-btn"
                  >
                    <BsPatchCheck className="me-1" />
                    ลายแทงมาตรา (แพ่ง)
                  </a>
                ) : null}

                {specialCourseDownloads.criminal ? (
                  <a
                    href={specialCourseDownloads.criminal.fileUrl || specialCourseDownloads.criminal.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="เปิดลายแทงมาตรา อาญา"
                    className="btn btn-sm btn-outline-light courseview-top-action-btn"
                  >
                    <BsPatchCheck className="me-1" />
                    ลายแทงมาตรา (อาญา)
                  </a>
                ) : null}
              </div>
              {!activeLesson ? (
                <>
                  <div className="courseview-player-shell">
                    <div className="courseview-player-frame courseview-empty-video-frame">
                      <div className="courseview-empty-video-skeleton">
                        <div className="courseview-empty-video-text">
                          ยังไม่มีบทเรียนที่เปิดให้แสดงผล
                        </div>
                      </div>
                    </div>
                  </div>

                  <CourseReviewForm courseId={courseId} courseName={course.title} enabled={course.reviewsEnabled === true} previewMode={previewMode} accessAllowed={accessReady && accessResult?.status === "allowed"} />

                  {renderCourseDownloads()}
                </>
              ) : (
                <>
                  {/* <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                    <Badge bg="primary">Lesson</Badge>
                    {activeLesson.isPreview ? <Badge bg="success">Preview</Badge> : null}
                    {activeLesson.contentStatus ? (
                      <Badge bg="light" text="dark">
                        {activeLesson.contentStatus}
                      </Badge>
                    ) : null}
                  </div> */}

                  <h5 className="mb-3">{activeLesson.title}</h5>

                  {activePlaybackProvider === PLAYBACK_PROVIDER.CLOUDFLARE && playerErr ? (
                    <Alert variant="danger">{playerErr}</Alert>
                  ) : (
                    <div className="courseview-player-shell">
                      <div className="courseview-player-frame">
                        {((activePlaybackProvider === PLAYBACK_PROVIDER.CLOUDFLARE && playerLoading) ||
                          (activePlaybackProvider === PLAYBACK_PROVIDER.MUX && muxPlayerLoading)) && (
                          <div className="courseview-player-loading">
                            <div className="courseview-player-skeleton" />
                            <div className="courseview-player-loading-text">
                              <Spinner animation="border" size="sm" /> กำลังโหลดวิดีโอ...
                            </div>
                          </div>
                        )}

                        {activePlaybackProvider === PLAYBACK_PROVIDER.MUX ? (
                          <>
                            <video
                              ref={bindMuxVideo}
                              className="courseview-mux-video"
                              controls
                              playsInline
                              preload="metadata"
                              onLoadedMetadata={(event) => applyMuxPlaybackRate(event.currentTarget)}
                              aria-label={activeLesson.title || "lesson-video-backup"}
                            />
                            {accessResult?.settings?.showWatermark && accessResult?.watermarkText ? (
                              <div
                                style={{
                                  position: "absolute", right: 12, bottom: 12, zIndex: 3,
                                  padding: "6px 10px", borderRadius: 10,
                                  background: "rgba(0,0,0,0.45)", color: "#fff",
                                  fontSize: 12, lineHeight: 1.2, pointerEvents: "none",
                                  userSelect: "none", backdropFilter: "blur(2px)",
                                  maxWidth: "80%", wordBreak: "break-word",
                                }}
                              >
                                {accessResult.watermarkText}
                              </div>
                            ) : null}
                          </>
                        ) : iframeUrl ? (
                          <>
                            <iframe
                              ref={bindCloudflareIframe}
                              key={playerKey}
                              src={iframeUrl}
                              title={activeLesson.title || "lesson-video"}
                              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                              allowFullScreen
                              className="courseview-player-iframe"
                              onLoad={() => {
                                setPlayerLoading(false);
                                setPlayerFrameLoaded(true);
                              }}
                            />

                            {accessResult?.settings?.showWatermark && accessResult?.watermarkText ? (
                              <div
                                style={{
                                  position: "absolute",
                                  right: 12,
                                  bottom: 12,
                                  zIndex: 3,
                                  padding: "6px 10px",
                                  borderRadius: 10,
                                  background: "rgba(0,0,0,0.45)",
                                  color: "#fff",
                                  fontSize: 12,
                                  lineHeight: 1.2,
                                  pointerEvents: "none",
                                  userSelect: "none",
                                  backdropFilter: "blur(2px)",
                                  maxWidth: "80%",
                                  wordBreak: "break-word",
                                }}
                              >
                                {accessResult.watermarkText}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <div className="courseview-player-empty">
                            ยังไม่พบลิงก์เล่นวิดีโอ
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activePlaybackProvider === PLAYBACK_PROVIDER.CLOUDFLARE &&
                    muxAvailability === "ready" && (previewMode || learnerStandbyEnabled) ? (
                    <div className="courseview-provider-switch mt-3">
                      <div className="small text-secondary">
                        {previewMode
                          ? "พบปัญหาในการรับชมวิดีโอหลัก?"
                          : "พบปัญหาเครือข่ายในการดูวิดีโอใช่หรือไม่?"}
                      </div>
                      <button type="button" className="courseview-provider-link"
                        onClick={switchToMuxPlayback}>
                        {previewMode
                          ? "เปลี่ยนไปใช้ระบบ Streaming สำรอง"
                          : "ใช้ระบบ Streaming สำรอง"}
                      </button>
                    </div>
                  ) : null}

                  {activePlaybackProvider === PLAYBACK_PROVIDER.MUX ? (
                    <div className={`courseview-mux-footer${muxPlaybackError ? " has-error" : ""}`}>
                      {activePlaybackProvider === PLAYBACK_PROVIDER.MUX &&
                        (previewMode || learnerStandbyEnabled) ? (
                        <div className="courseview-provider-switch mt-3">
                          {previewMode ? (
                            <div className="small text-secondary">กำลังใช้ระบบ Streaming สำรอง</div>
                          ) : null}
                          {muxPlaybackError ? (
                            <div className="small text-warning">
                              {muxPlaybackError}
                              <MuxFailureHelp key={muxPlaybackDiagnosticId} correlationId={muxPlaybackDiagnosticId} />
                            </div>
                          ) : (
                            <div className="small text-secondary">
                              {previewMode
                                ? "ต้องการกลับไปใช้ระบบวิดีโอหลัก?"
                                : "ต้องการใช้ระบบดูติวหลักหรือไม่?"}
                            </div>
                          )}
                          <button type="button" className="courseview-provider-link"
                            onClick={switchToCloudflarePlayback}>
                            {muxPlaybackError
                              ? "กลับไปใช้ระบบวิดีโอหลัก"
                              : previewMode
                                ? "เปลี่ยนไปใช้ระบบ Streaming หลัก"
                                : "ใช้ระบบ Streaming หลัก"}
                          </button>
                        </div>
                      ) : null}
                      <div className="courseview-mux-speed">
                        <label htmlFor="courseview-mux-speed">ความเร็ว</label>
                        <select
                          id="courseview-mux-speed"
                          aria-label="ความเร็วในการเล่น"
                          value={muxPlaybackRate}
                          disabled={!!muxPlaybackError}
                          onChange={(event) => applyMuxPlaybackRate(muxVideoRef.current, Number(event.target.value))}
                        >
                          {[0.75, 1, 1.25, 1.5, 1.75, 2].map((rate) => (
                            <option key={rate} value={rate}>{rate}x</option>
                          ))}
                          {![0.75, 1, 1.25, 1.5, 1.75, 2].includes(muxPlaybackRate) ? (
                            <option value={muxPlaybackRate}>{muxPlaybackRate}x</option>
                          ) : null}
                        </select>
                      </div>
                      {muxPlaybackRateNotice ? <div className="courseview-mux-speed-notice small" role="status">{muxPlaybackRateNotice}</div> : null}
                    </div>
                  ) : null}

                  {!previewMode && learnerStandbyEnabled &&
                    preferredPlaybackProvider === PLAYBACK_PROVIDER.MUX &&
                    activePlaybackProvider === PLAYBACK_PROVIDER.CLOUDFLARE &&
                    muxAvailability === "unavailable" ? (
                    <div className="courseview-provider-switch mt-3">
                      <div className="small text-secondary">
                        ระบบ Streaming สำรองยังไม่พร้อมสำหรับวิดีโอนี้ กำลังใช้ระบบวิดีโอหลัก
                      </div>
                    </div>
                  ) : null}

                  {activePlaybackProvider === PLAYBACK_PROVIDER.CLOUDFLARE && showVideoHelp ? (
                    <div className="courseview-video-help mt-2">
                      <div>
                        หากวิดีโอไม่แสดง กรุณารีโหลดหน้านี้ใหม่ หรือรีโหลดวิดีโอ
                        {" "}
                        <Link to="/help" className="courseview-video-help-link">
                          ดูวิธีแก้ไข
                        </Link>
                      </div>

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-light mt-2"
                        onClick={reloadVideoPlayer}
                      >
                        รีโหลดวิดีโอ
                      </button>
                    </div>
                  ) : null}

                  {overdueInstallmentEnrolls.length > 0 && (
                    <div className="small text-secondary mt-3">
                      กรุณาชำระเงินแพ็กเกจ{" "}
                      <strong>
                        {overdueInstallmentEnrolls[0]?.coursePackageLabel || overdueInstallmentEnrolls[0]?.coursePackage || "-"}
                      </strong>{" "}
                      งวดที่{" "}
                      <strong>
                        {getCurrentUnpaidInstallment(overdueInstallmentEnrolls[0])?.installmentNo || "-"}
                      </strong>{" "}
                      เพื่อดูเนื้อหาส่วนนี้ต่อไป{" "}
                      <Link
                        to={`/courses/${course.id}/payment-promptpay/${overdueInstallmentEnrolls[0]?.id}`}
                        className="ms-1"
                      >
                        ไปหน้าชำระเงิน
                      </Link>
                    </div>
                  )}

                  <CourseReviewForm courseId={courseId} courseName={course.title} enabled={course.reviewsEnabled === true} previewMode={previewMode} accessAllowed={accessReady && accessResult?.status === "allowed"} />

                  {safeArray(activeLesson.documents).length > 0 ? (
                    <div className="mt-4">
                      <div className="fw-semibold mb-2">เอกสารประกอบ</div>
                      <div className="d-flex flex-column gap-2">
                        {safeArray(activeLesson.documents).map((docFile, idx) => {
                          const docHref = docFile?.url || docFile?.fileUrl || "";

                          return (
                            <div
                              key={`${activeLesson.id}-${idx}`}
                              className="courseview-file-box courseview-lesson-doc-box d-flex align-items-center justify-content-between gap-2"
                            >
                              <div className="min-w-0">
                                <div className="fw-semibold text-break">
                                  {stripPdfExt(docFile?.title || docFile?.name || `เอกสาร ${idx + 1}`)}
                                </div>
                                <div className="courseview-file-meta">
                                  {docFile?.description || docFile?.ext?.toUpperCase() || "PDF"}
                                </div>
                              </div>

                              {docHref ? (
                                <a
                                  href={docHref}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="btn btn-sm courseview-doc-open-btn"
                                >
                                  ดาวน์โหลดสไลด์
                                </a>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}

                  {renderCourseDownloads()}
                </>
              )}
            </Card.Body>
          </Card>
        </div>
        </Col>

        <Col lg={4}>
          <Card className="courseview-card">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                <div className="fw-semibold">บทเรียนทั้งหมด</div>

                

                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={toggleExpandAllCurriculum}
                  className="d-inline-flex d-lg-none"
                >
                  {curriculumExpandedAll ? "ย่อทั้งหมด" : "แสดงทั้งหมด"}
                </Button>
              </div>

              {sidebarLoading && visibleHeaders.length === 0 ? (
                <div className="text-center text-secondary py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  กำลังโหลดรายการบทเรียน...
                </div>
              ) : visibleHeaders.length === 0 ? (
                <Alert variant="secondary" className="mb-0">
                  ยังไม่มีบทเรียนที่เปิดให้แสดงผล
                </Alert>
              ) : (
                
                  <div className="cc-curr">
                    {safeArray(visibleHeaders).map((h, hi) => {
                      const headerKey = String(hi);
                      // const headerOpen = openSidebarHeaderKey === headerKey;
                      const headerOpen =
                        curriculumExpandedAll || openSidebarHeaderKey === headerKey;

                      return (
                        <div key={h.id || headerKey}>
                          <button
                            type="button"
                            className={`cc-row ${headerOpen ? "is-open" : ""}`}
                            /* onClick={() => {
                              if (headerOpen) {
                                setOpenSidebarHeaderKey("");
                              } else {
                                setOpenSidebarHeaderKey(headerKey);
                                setOpenSidebarSectionKey(`${hi}-0`);
                              }
                            }} */
                           onClick={() => {
                              userTouchedSidebarRef.current = true;
                              setCurriculumExpandedAll(false);

                              if (headerOpen && !curriculumExpandedAll) {
                                setOpenSidebarHeaderKey("");
                              } else {
                                setOpenSidebarHeaderKey(headerKey);
                                setOpenSidebarSectionKey(`${hi}-0`);
                              }
                            }}
                          >
                            <div>
                              <div className="cc-title cc-header-title">
                                {h?.title || "Header"}
                              </div>
                              <div
                                className={`cc-sub ${
                                  safeArray(h?.sections).length > 0 ? "cc-sub-accent" : ""
                                }`}
                              >
                                {safeArray(h?.sections).length} ข้อบทเรียน
                              </div>
                            </div>
                            <div
                              className={`cc-chevron ${
                                headerOpen ? "is-open" : "is-closed"
                              }`}
                            >
                              {headerOpen ? <BsChevronDown /> : <BsChevronRight />}
                            </div>
                          </button>

                          <div className={`cc-panel ${headerOpen ? "is-open" : ""}`}>
                            <div className="cc-panel-inner">
                              {safeArray(h.sections).map((sec, si) => {
                                const secKey = `${hi}-${si}`;
                                // const secOpen = openSidebarSectionKey === secKey;
                                const secOpen =
                                  curriculumExpandedAll || openSidebarSectionKey === secKey;

                                const hasItems = safeArray(sec?.items).length > 0;

                             

                                /* const hasTodayRelease = safeArray(sec.items).some((item) => {
                                  const raw =
                                    item?.releaseDateText ||
                                    item?.releaseAt ||
                                    item?.publishedAt;

                                  if (!raw) return false;

                                  const d = raw?.toDate ? raw.toDate() : new Date(raw);

                                  if (Number.isNaN(d.getTime())) return false;

                                  const now = new Date();

                                  return (
                                    d.getFullYear() === now.getFullYear() &&
                                    d.getMonth() === now.getMonth() &&
                                    d.getDate() === now.getDate()
                                  );
                                }); */

                                return (
                                  <div
                                    key={sec.id || secKey}
                                    // className={`cc-sec ${hasTodayRelease ? "has-new-release" : ""}`}
                                    className={`cc-sec ${hasItems ? "has-visible-lessons" : ""}`}
                                  >
                                    <button
                                      type="button"
                                      className={`cc-secbtn ${secOpen ? "is-active" : ""}`}
                                      /* onClick={() =>
                                        setOpenSidebarSectionKey(secOpen ? "" : secKey)
                                      } */
                                     onClick={() => {
                                        userTouchedSidebarRef.current = true;
                                        setCurriculumExpandedAll(false);
                                        setOpenSidebarSectionKey(secOpen && !curriculumExpandedAll ? "" : secKey);
                                      }}
                                    >
                                      <div>
                                        <div className="cc-title cc-section-title" style={{ fontSize: 14 }}>
                                          {sec?.title || "Section"}
                                        </div>
                                        <div
                                          className={`cc-sub ${
                                            safeArray(sec?.items).length > 0 ? "cc-sub-accent" : ""
                                          }`}
                                        >
                                          {safeArray(sec?.items).length} จำนวน EP
                                        </div>
                                      </div>
                                      <div
                                        className={`cc-chevron cc-section-chevron ${
                                          secOpen ? "is-open" : "is-closed"
                                        } ${
                                          safeArray(sec?.items).length > 0 ? "has-items" : "no-items"
                                        }`}
                                      >
                                        {secOpen ? <BsChevronDown /> : <BsChevronRight />}
                                      </div>
                                    </button>

                                    <div className={`cc-secbody ${secOpen ? "is-open" : ""}`}>
                                      <div className="cc-secbody-inner">
                                        {safeArray(sec.items).map((item) => {
                                          const indicator = getLessonIndicator(item, lastProgress);
                                          const meta = getLessonMeta(item);
                                          const isActive = activeLesson?.id === item.id;

                                          return (
                                            <button
                                              key={item.id}
                                              type="button"
                                              className="cc-lesson-btn"
                                              onClick={() => {
                                                if (activeLesson?.id !== item.id) {
                                                  setActiveLesson(item);
                                                }
                                              }}
                                            >
                                              <div className={`cc-lesson-item ${isActive ? "is-active" : ""}`}>
                                                <div
                                                  style={{
                                                    width: 18,
                                                    flex: "0 0 18px",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center", // ✅ เพิ่มอันนี้
                                                    paddingTop: 0,        // ✅ เอาออก
                                                  }}
                                                >
                                                  {indicator.kind === "completed" ? (
                                                    <BsCheckLg className="cc-lesson-icon-done" />
                                                  ) : indicator.kind === "in_progress" ? (
                                                    <BsPlayCircle className="cc-lesson-icon-progress" />
                                                  ) : (
                                                    <span
                                                      style={{
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: "50%",
                                                        display: "inline-block",
                                                        ...getLessonBulletStyle(indicator.status),
                                                      }}
                                                    />
                                                  )}
                                                </div>

                                                <div className="cc-lesson-main">
                                                  <div className="cc-lesson-title text-break">
                                                    {/* {item.title || "-"} */}
                                                    {stripLessonPrefix(item.title) || "-"}
                                                  </div>
                                                </div>

                                                <div className="cc-lesson-meta">
                                                  <span
                                                    className={
                                                      meta.kind === "duration"
                                                        ? "small text-nowrap cc-lesson-duration"
                                                        : "small text-nowrap cc-lesson-date"
                                                    }
                                                  >
                                                    {meta.text}
                                                  </span>
                                                </div>
                                              </div>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {hi < safeArray(visibleHeaders).length - 1 ? <div className="cc-divider" /> : null}
                        </div>
                      );
                    })}
                  </div>
                
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal
        show={!!pendingSummaryDownloadGroup}
        onHide={() => setPendingSummaryDownloadGroup(null)}
        centered
        animation={false}
        contentClassName="courseview-modal-dark"
        aria-labelledby="summary-download-warning-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="summary-download-warning-title">
            ชีทนี้ความลับค่ะ 🤫
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          อย่าแชร์ เดี๋ยวอาจารย์เห็นแล้วออกหลบ!
          <br />
          เก็บไว้รู้กันเองในบ้านเรานะคะ 😂🖤
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="success"
            onClick={() => {
              setDownloadModalGroup(pendingSummaryDownloadGroup);
              setPendingSummaryDownloadGroup(null);
            }}
          >
            ตกลง
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!downloadModalGroup}
        onHide={() => setDownloadModalGroup(null)}
        centered
        size="lg"
        contentClassName="courseview-modal-dark"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {downloadModalGroup?.title || "เอกสารดาวน์โหลด"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="d-flex flex-column gap-2">
            {safeArray(downloadModalGroup?.files).map((f, idx) => {
              const fileHref = f?.url || f?.fileUrl || "";

              return (
                <div
                  key={f?.id || f?.storagePath || `${downloadModalGroup?.scope}-${idx}`}
                  className="courseview-file-box d-flex align-items-center justify-content-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="fw-semibold text-break">
                      {f?.title || f?.name || `เอกสาร ${idx + 1}`}
                    </div>
                    <div className="courseview-file-meta">
                      {f?.ext?.toUpperCase() || "FILE"}
                      {f?.fileName ? ` · ${f.fileName}` : ""}
                    </div>
                  </div>

                  {fileHref ? (
                    <a
                      href={fileHref}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-sm btn-outline-light"
                    >
                      เปิดเอกสาร
                    </a>
                  ) : null}
                </div>
              );
            })}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setDownloadModalGroup(null)}>
            ปิด
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!pendingReplace} onHide={() => setPendingReplace(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>ยืนยันเปลี่ยนอุปกรณ์</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            ตรวจพบอุปกรณ์ประเภท <strong>{pendingReplace?.device?.slot}</strong> เครื่องใหม่
          </p>
          <p className="mb-0">
            หากยืนยัน ระบบจะผูกสิทธิกับอุปกรณ์นี้ และเริ่มรอบเวลาการเปลี่ยนอุปกรณ์ใหม่
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setPendingReplace(null)}
            disabled={accessChecking}
          >
            ยกเลิก
          </Button>
          <Button
            variant="primary"
            disabled={accessChecking}
            onClick={async () => {
              try {
                if (!pendingReplace?.device) return;

                setAccessChecking(true);

                const result = await confirmReplaceAccessDevice({
                  user,
                  userDoc,
                  courseId,
                  settings: pendingReplace?.settings,
                  device: pendingReplace?.device,
                });

                setAccessResult(result);
                setPendingReplace(null);
              } catch (e) {
                setAccessResult({
                  status: "denied",
                  ok: false,
                  message: e?.message || "ยืนยันเปลี่ยนอุปกรณ์ไม่สำเร็จ",
                  watermarkText: "",
                  settings: null,
                });
                setPendingReplace(null);
              } finally {
                setAccessChecking(false);
              }
            }}
          >
            {accessChecking ? "กำลังยืนยัน..." : "ยืนยันเปลี่ยนอุปกรณ์"}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showQrModal}
        onHide={() => setShowQrModal(false)}
        centered
        contentClassName="courseview-modal-dark"
      >
        <Modal.Header closeButton>
          <Modal.Title>เข้าร่วมกลุ่มติว</Modal.Title>
        </Modal.Header>

        <Modal.Body className="text-center">
          {lineGroupQrImageUrl ? (
            <>
              <img
                src={lineGroupQrImageUrl}
                alt="QR กลุ่มติว"
                style={{
                  maxWidth: "100%",
                  width: 320,
                  borderRadius: 12,
                }}
              />

              <div className="mt-3 text-secondary small">
                สแกนเพื่อเข้าร่วมกลุ่ม Line
              </div>
            </>
          ) : (
            <div className="text-secondary small">
              ยังไม่มี QR กลุ่มติวสำหรับคอร์สนี้
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showScheduleModal}
        onHide={() => setShowScheduleModal(false)}
        centered
        size="xl"
        contentClassName="courseview-modal-dark courseview-image-preview-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <BsCalendar2Heart className="me-2" />
            ตารางเรียน
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {scheduleImages[schedulePreviewIndex]?.imageUrl ? (
            <div className="courseview-preview-main-wrap">
              <img
                src={scheduleImages[schedulePreviewIndex].imageUrl}
                alt={scheduleImages[schedulePreviewIndex]?.name || "ตารางเรียน"}
                className="courseview-preview-main-image"
              />
            </div>
          ) : null}

          <div className="courseview-preview-thumb-row">
            {scheduleImages.map((img, index) => (
              <button
                key={img.id || img.imageUrl || index}
                type="button"
                className={`courseview-preview-thumb ${
                  index === schedulePreviewIndex ? "is-active" : ""
                }`}
                onClick={() => setSchedulePreviewIndex(index)}
              >
                <img src={img.imageUrl} alt={img.name || "thumbnail"} />
              </button>
            ))}
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={showCalendarModal}
        onHide={() => setShowCalendarModal(false)}
        centered
        size="xl"
        contentClassName="courseview-modal-dark courseview-image-preview-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <BsCalendar2Check className="me-2" />
            ปฏิทินคอร์ส
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {calendarImages[calendarPreviewIndex]?.imageUrl ? (
            <div className="courseview-preview-main-wrap">
              <img
                src={calendarImages[calendarPreviewIndex].imageUrl}
                alt={calendarImages[calendarPreviewIndex]?.name || "ปฏิทินคอร์ส"}
                className="courseview-preview-main-image"
              />
            </div>
          ) : null}

          <div className="courseview-preview-thumb-row">
            {calendarImages.map((img, index) => (
              <button
                key={img.id || img.imageUrl || index}
                type="button"
                className={`courseview-preview-thumb ${
                  index === calendarPreviewIndex ? "is-active" : ""
                }`}
                onClick={() => setCalendarPreviewIndex(index)}
              >
                <img src={img.imageUrl} alt={img.name || "thumbnail"} />
              </button>
            ))}
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={showChecklistModal}
        onHide={() => setShowChecklistModal(false)}
        centered
        size="xl"
        contentClassName="courseview-modal-dark courseview-image-preview-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <BsCalendar2Check className="me-2" />
            Checklist การเรียน
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {checklistImages[checklistPreviewIndex]?.imageUrl ? (
            <div className="courseview-preview-main-wrap">
              <img
                src={checklistImages[checklistPreviewIndex].imageUrl}
                alt={checklistImages[checklistPreviewIndex]?.name || "Checklist การเรียน"}
                className="courseview-preview-main-image"
              />
            </div>
          ) : null}

          <div className="courseview-preview-thumb-row">
            {checklistImages.map((img, index) => (
              <button
                key={img.id || img.imageUrl || index}
                type="button"
                className={`courseview-preview-thumb ${
                  index === checklistPreviewIndex ? "is-active" : ""
                }`}
                onClick={() => setChecklistPreviewIndex(index)}
              >
                <img src={img.imageUrl} alt={img.name || "thumbnail"} />
              </button>
            ))}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
